import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Plus, MapPin, Star, MessageSquare, Phone, Video, DollarSign, Share2, Grid, X, Check, Shield, ChevronDown, Award, TrendingUp, Search as SearchIcon, Users, Eye, Calendar, Link2, Copy, ExternalLink, Wallet, QrCode, Gift, ArrowUpRight, ArrowDownLeft, Heart, FileText, Clock, BookmarkIcon, Upload, Trash2, BarChart3, Edit3, Radio, Bell, MoreHorizontal, Send } from 'lucide-react';
import { openRazorpayCheckout } from '../../utils/razorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BRANCHES = 7;
const CURRENCY_SYMBOLS = { INR:'₹', USD:'$', EUR:'€', GBP:'£', AED:'د.إ', AUD:'A$', CAD:'C$', SGD:'S$', JPY:'¥', CNY:'¥', KRW:'₩', BRL:'R$', MXN:'MX$', ZAR:'R', SAR:'﷼', THB:'฿', PHP:'₱', IDR:'Rp', MYR:'RM', NGN:'₦', KES:'KSh', BDT:'৳', PKR:'₨', LKR:'Rs', NPR:'₨', EGP:'E£', TRY:'₺' };
export const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || (code || '₹');
const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner', color: 'bg-blue-500/15 text-blue-400' },
  { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-500/15 text-yellow-400' },
  { value: 'expert', label: 'Expert', color: 'bg-green-500/15 text-green-400' },
];
const DEPTH_COLORS = { Newcomer: 'text-zinc-500', Explorer: 'text-blue-400', Practitioner: 'text-yellow-400', Specialist: 'text-purple-400', Authority: 'text-orange-400' };

export default function ProfileTab({ user, isOwnProfile, viewUserId, refreshUser, showToast, onBack, onProfileClick, onSwitchTab }) {
  const navigate = useNavigate();
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [depthScore, setDepthScore] = useState(null);
  const [insights, setInsights] = useState([]);
  const [calendarLinked, setCalendarLinked] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showMonetization, setShowMonetization] = useState(false);
  const [showAddExpertise, setShowAddExpertise] = useState(false);
  const [showVerifyQueue, setShowVerifyQueue] = useState(false);
  const [community, setCommunity] = useState(null);
  const [showCommunity, setShowCommunity] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastText, setBroadcastText] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [showWallet, setShowWallet] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(250);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [showEarningsDetail, setShowEarningsDetail] = useState(false);
  const [showSpentDetail, setShowSpentDetail] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');
  const [suggestionSent, setSuggestionSent] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showFollowersList, setShowFollowersList] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showBookingFlow, setShowBookingFlow] = useState(false);
  const [bookingCallType, setBookingCallType] = useState('video');  // 'video' | 'voice'
  const [userGroupEvents, setUserGroupEvents] = useState([]);
  const [joiningEvent, setJoiningEvent] = useState(null);
  const [showDotMenu, setShowDotMenu] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const displayUser = profileUser || user;
  const isOwn = isOwnProfile && !viewUserId;

  useEffect(() => {
    if (viewUserId && viewUserId !== user?.user_id) {
      fetchOtherUser(viewUserId);
    } else {
      setProfileUser(null);
    }
  }, [viewUserId]);

  useEffect(() => { fetchPosts(); fetchDepthScore(); if (isOwn) { fetchInsights(); fetchCalendarStatus(); fetchWallet(); } else { fetchUserGroupEvents(); } fetchCommunity(); fetchCertificates(); }, [displayUser?.user_id]);

  // Fetch certificates from API
  const fetchCertificates = async () => {
    if (!displayUser?.user_id) return;
    try {
      const res = await fetch(`${API_URL}/api/certificates/${displayUser.user_id}`);
      if (res.ok) {
        const certs = await res.json();
        if (profileUser) setProfileUser(prev => prev ? { ...prev, certificates: certs } : prev);
      }
    } catch {}
  };

  const fetchUserGroupEvents = async () => {
    try {
      const uid = displayUser?.user_id || viewUserId;
      if (!uid) return;
      const res = await fetch(`${API_URL}/api/calls/group-events/user/${uid}`);
      if (res.ok) setUserGroupEvents(await res.json());
    } catch {}
  };

  const handleJoinGroupEvent = async (evt) => {
    if (!evt.event_id) return;
    setJoiningEvent(evt.event_id);
    try {
      // Pay via Razorpay first
      if (evt.price > 0) {
        const payRes = await fetch(`${API_URL}/api/payments/checkout`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ expert_id: evt.host_id, payment_type: 'group_session', amount_override: evt.price }),
        });
        if (!payRes.ok) { showToast?.('Payment setup failed'); setJoiningEvent(null); return; }
        const pd = await payRes.json();
        const rzpRes = await openRazorpayCheckout({
          order_id: pd.order_id, amount: pd.amount, currency: pd.currency,
          key_id: pd.key_id, name: 'Connect',
          description: `${evt.topic || evt.title} — ₹${evt.price}`,
          prefill: { name: user?.name, email: user?.email },
        });
        const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...rzpRes, txn_id: pd.txn_id }),
        });
        if (!verifyRes.ok) { showToast?.('Payment verification failed'); setJoiningEvent(null); return; }
      }
      // Auto-join after payment — no host approval needed
      const joinRes = await fetch(`${API_URL}/api/calls/group-events/${evt.event_id}/join`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (joinRes.ok) { showToast?.('Joined! You\'re in.'); fetchUserGroupEvents(); }
      else { const e = await joinRes.json(); showToast?.(e.detail || 'Failed to join'); }
    } catch (e) {
      if (e.message !== 'Payment cancelled') showToast?.(e.message || 'Something went wrong');
    }
    setJoiningEvent(null);
  };

  const fetchWallet = async () => {
    try {
      const res = await fetch(`${API_URL}/api/wallet`, { headers });
      if (res.ok) setWalletData(await res.json());
    } catch {}
  };

  const topUpWallet = async () => {
    setTopUpLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/wallet/topup`, {
        method: 'POST', headers,
        body: JSON.stringify({ amount: topUpAmount }),
      });
      if (res.ok) {
        const d = await res.json();
        const rzpResponse = await openRazorpayCheckout({
          order_id: d.order_id, amount: d.amount, currency: d.currency,
          key_id: d.key_id, name: 'Connect',
          description: `Wallet Top-up ₹${topUpAmount}`,
          prefill: { name: user?.name, email: user?.email },
        });
        const verifyRes = await fetch(`${API_URL}/api/wallet/topup/verify`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...rzpResponse, txn_id: d.txn_id }),
        });
        if (verifyRes.ok) {
          const result = await verifyRes.json();
          if (showToast) showToast(`Wallet topped up! Balance: ₹${result.balance}`);
          fetchWallet();
          refreshUser?.();
        }
      }
    } catch (e) {
      if (e.message !== 'Payment cancelled' && showToast) showToast(e.message || 'Top-up failed');
    }
    setTopUpLoading(false);
  };

  const handlePriceTagClick = async (paymentType) => {
    if (isOwn || !displayUser?.user_id) return;
    if (paymentType === 'dm') {
      // Navigate to chat with this user
      if (onProfileClick) onProfileClick(displayUser.user_id);
      return;
    }
    // Voice / Video Call: open Book a Session flow (date → slot → note → payment)
    if (paymentType === 'voice_call' || paymentType === 'video_call') {
      setBookingCallType(paymentType === 'video_call' ? 'video' : 'voice');
      setShowBookingFlow(true);
      return;
    }
    // Voice Note keeps direct payment (it's an async one-off message, no scheduling needed)
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST', headers,
        body: JSON.stringify({ expert_id: displayUser.user_id, payment_type: paymentType }),
      });
      if (res.ok) {
        const d = await res.json();
        const rzpResponse = await openRazorpayCheckout({
          order_id: d.order_id, amount: d.amount, currency: d.currency,
          key_id: d.key_id, name: 'Connect',
          description: `${paymentType.replace('_', ' ')} with ${d.expert_name}`,
          prefill: { name: user?.name, email: user?.email },
        });
        const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...rzpResponse, txn_id: d.txn_id }),
        });
        if (verifyRes.ok) {
          if (showToast) showToast('Payment successful! Booking created.');
        }
      }
    } catch (e) {
      if (e.message !== 'Payment cancelled' && showToast) showToast(e.message || 'Payment failed');
    }
    setPaymentLoading(false);
  };

  const fetchFollowers = async (type) => {
    try {
      const uid = displayUser?.user_id || user?.user_id;
      const res = await fetch(`${API_URL}/api/social/${uid}/${type}`, { headers });
      if (res.ok) { setFollowersList(await res.json()); setShowFollowersList(type); }
    } catch {}
  };


  const applyReferral = async () => {
    if (!referralInput.trim()) return;
    setReferralLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/profile/apply-referral?code=${encodeURIComponent(referralInput)}`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        if (showToast) showToast(d.message);
        refreshUser?.();
        setReferralInput('');
      } else {
        const err = await res.json();
        if (showToast) showToast(err.detail || 'Invalid code');
      }
    } catch {}
    setReferralLoading(false);
  };

  const fetchQr = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/qr/${displayUser?.user_id}?payment_type=video_call`, { headers });
      if (res.ok) { setQrData(await res.json()); setShowQr(true); }
    } catch {}
  };

  const fetchOtherUser = async (uid) => {
    try {
      const res = await fetch(`${API_URL}/api/profile/id/${uid}`, { headers });
      if (res.ok) setProfileUser(await res.json());
    } catch {}
  };

  const fetchCommunity = async () => {
    const uid = viewUserId || user?.user_id;
    try {
      const res = await fetch(`${API_URL}/api/communities/${uid}`, { headers });
      if (res.ok) {
        const c = await res.json();
        setCommunity(c);
        // Fetch broadcasts
        const bRes = await fetch(`${API_URL}/api/communities/${c.community_id}/broadcasts`, { headers });
        if (bRes.ok) setBroadcasts(await bRes.json());
      } else { setCommunity(null); }
    } catch { setCommunity(null); }
  };

  const createCommunity = async () => {
    try {
      const res = await fetch(`${API_URL}/api/communities`, { method: 'POST', headers });
      if (res.ok) { setCommunity(await res.json()); showToast('Community created!'); }
    } catch {}
  };

  const joinCommunity = async () => {
    if (!community) return;
    try {
      await fetch(`${API_URL}/api/communities/${community.community_id}/join`, { method: 'POST', headers });
      fetchCommunity();
      showToast('Joined community!');
    } catch {}
  };

  const sendBroadcast = async () => {
    if (!broadcastText.trim() || !community) return;
    try {
      const res = await fetch(`${API_URL}/api/communities/${community.community_id}/broadcast?content=${encodeURIComponent(broadcastText)}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        const b = await res.json();
        setBroadcasts(prev => [b, ...prev]);
        setBroadcastText('');
        showToast('Broadcast sent!');
      }
    } catch {}
  };

  const fetchPosts = async () => {
    const uid = displayUser?.user_id || user?.user_id;
    if (!uid) return;
    try { const res = await fetch(`${API_URL}/api/posts/user/${uid}`); if (res.ok) setPosts(await res.json()); } catch {}
  };
  const fetchDepthScore = async () => {
    const uid = displayUser?.user_id || user?.user_id;
    if (!uid) return;
    try { const res = await fetch(`${API_URL}/api/profile/${uid}/depth-score`); if (res.ok) setDepthScore(await res.json()); } catch {}
  };
  const fetchInsights = async () => {
    try { const res = await fetch(`${API_URL}/api/insights/weekly`, { headers }); if (res.ok) { const d = await res.json(); setInsights(d.insights || []); } } catch {}
  };
  const fetchCalendarStatus = async () => {
    try { const res = await fetch(`${API_URL}/api/calendar/status`, { headers }); if (res.ok) { const d = await res.json(); setCalendarLinked(d.linked); } } catch {}
  };
  const linkCalendar = async () => {
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/start`, { headers });
      if (res.ok) { const d = await res.json(); window.location.href = d.authorization_url; }
    } catch {}
  };
  const unlinkCalendar = async () => {
    try { await fetch(`${API_URL}/api/calendar/unlink`, { method: 'POST', headers }); setCalendarLinked(false); } catch {}
  };
  const handleShare = async () => {
    const username = displayUser?.username || user?.username;
    if (!username) return;
    const shareUrl = `${window.location.origin}/u/@${username}`;
    const shareTitle = displayUser?.name || user?.name || 'Connect Profile';
    const shareText = displayUser?.headline || '';
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        if (showToast) showToast('Profile link copied!');
      } else {
        setShareLink(shareUrl);
        setShowShareModal(true);
      }
    } catch (e) {
      // User cancelled share or error — try clipboard fallback
      try {
        await navigator.clipboard.writeText(shareUrl);
        if (showToast) showToast('Profile link copied!');
      } catch {
        setShareLink(shareUrl);
        setShowShareModal(true);
      }
    }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    if (showToast) showToast('Link copied!');
  };

  const hasVerified = (displayUser?.expertise_tree || []).some(b => b.verified);
  const hasPeerVerified = (displayUser?.expertise_tree || []).some(b => b.peer_verified);
  const branchCount = (displayUser?.expertise_tree || []).length;

  const updateSkillLevel = async (branchId, level) => {
    await fetch(`${API_URL}/api/expertise/${branchId}/level?level=${level}`, { method: 'PUT', headers });
    refreshUser?.();
  };

  const removeBranch = async (branchId) => {
    await fetch(`${API_URL}/api/expertise/${branchId}`, { method: 'DELETE', headers });
    refreshUser?.();
    fetchDepthScore();
  };

  return (
    <div className="h-full overflow-y-auto bg-black" data-testid="profile-tab">
      {/* Back button for viewing other profiles */}
      {!isOwn && onBack && (
        <div className="px-4 pt-3 pb-1">
          <button onClick={onBack} className="flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="profile-back-btn">
            <span className="text-lg">&larr;</span> Back
          </button>
        </div>
      )}
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between mb-4">
          <span className="font-bold text-sm">@{displayUser?.username || 'username'}</span>
          {isOwn && (
            <button onClick={() => setShowSettings(true)} className="text-zinc-400 hover:text-white" data-testid="settings-btn"><Settings className="w-5 h-5" /></button>
          )}
        </div>
        <div className="flex items-center space-x-5">
          {/* Profile Picture */}
          {/* Profile Picture — tap to upload on own profile */}
          <div className={`relative w-24 h-24 rounded-full shrink-0 p-[3px] ${hasPeerVerified ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-[0_0_24px_rgba(251,191,36,0.5)]' : hasVerified ? 'bg-gradient-to-br from-yellow-400 to-orange-500 shadow-[0_0_12px_rgba(251,191,36,0.3)]' : 'bg-gradient-to-br from-purple-500 to-orange-400'}`} data-testid="profile-picture">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={() => displayUser?.picture && setShowPhotoModal(true)}>
              {displayUser?.picture ? <img src={`${API_URL}${displayUser.picture}`} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform" /> : <span className="text-2xl font-bold">{displayUser?.name?.[0]}</span>}
            </div>
            {isOwn && (
              <>
                <label htmlFor="avatar-upload" className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer border-2 border-black shadow-lg hover:bg-zinc-200 transition-colors" data-testid="upload-avatar-btn">
                  <Upload className="w-4 h-4 text-black" />
                </label>
                <input id="avatar-upload" type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const fd = new FormData(); fd.append('file', file);
                  try {
                    const res = await fetch(`${API_URL}/api/profile/picture`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
                    if (res.ok) { if (showToast) showToast('Profile picture updated!'); refreshUser?.(); }
                    else { if (showToast) showToast('Upload failed'); }
                  } catch { if (showToast) showToast('Upload error'); }
                }} />
              </>
            )}
          </div>
          <div className="flex items-center space-x-6">
            {(isOwn || displayUser?.privacy_settings?.show_community !== false) && (
              <button onClick={() => fetchFollowers('community')} className="text-center hover:opacity-80 transition-opacity" data-testid="stat-community"><p className="font-bold text-lg">{community?.member_count || 0}</p><p className="text-[10px] text-zinc-500">Community</p></button>
            )}
            {(isOwn || displayUser?.privacy_settings?.show_connected !== false) && (
              <button onClick={() => fetchFollowers('connected')} className="text-center hover:opacity-80 transition-opacity" data-testid="stat-connected"><p className="font-bold text-lg">{displayUser?.connected_count || 0}</p><p className="text-[10px] text-zinc-500">Connected</p></button>
            )}
            {(isOwn || displayUser?.privacy_settings?.show_connecting !== false) && (
              <button onClick={() => fetchFollowers('connecting')} className="text-center hover:opacity-80 transition-opacity" data-testid="stat-connecting"><p className="font-bold text-lg">{displayUser?.connecting_count || 0}</p><p className="text-[10px] text-zinc-500">Connecting</p></button>
            )}
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center space-x-2">
            <p className="font-bold text-sm">{displayUser?.name}</p>
            {displayUser?.is_blue_verified && (
              <svg className="w-4 h-4 text-blue-500 shrink-0" viewBox="0 0 22 22" fill="currentColor" data-testid="blue-tick">
                <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.141.27.587.7 1.086 1.24 1.44s1.167.551 1.813.568c.645-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.224 1.26.273 1.896.143.636-.13 1.22-.436 1.69-.88.445-.47.75-1.055.88-1.69.13-.634.08-1.29-.14-1.898.587-.273 1.084-.704 1.438-1.244.355-.54.552-1.17.57-1.817zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"/>
              </svg>
            )}
            {hasPeerVerified && !displayUser?.is_blue_verified && <Shield className="w-4 h-4 text-yellow-400" />}
          </div>
          {displayUser?.headline && <p className="text-xs text-zinc-400">{displayUser.headline}</p>}
          {(displayUser?.position || displayUser?.company) && (
            <p className="text-xs text-zinc-400 mt-0.5">
              {displayUser.position}{displayUser.position && displayUser.company ? ' at ' : ''}{displayUser.company}
            </p>
          )}
          {displayUser?.bio && <p className="text-xs text-zinc-300 mt-1">{displayUser.bio}</p>}
          <div className="flex items-center space-x-3 mt-1 text-[10px] text-zinc-500">
            {displayUser?.city && <span className="flex items-center"><MapPin className="w-3 h-3 mr-0.5" />{displayUser.city}{displayUser.country ? `, ${displayUser.country}` : ''}</span>}
            {displayUser?.rating > 0 && <span className="flex items-center"><Star className="w-3 h-3 mr-0.5 text-yellow-400" />{displayUser.rating.toFixed(1)}</span>}
            {displayUser?.education && <span className="flex items-center"><Award className="w-3 h-3 mr-0.5" />{displayUser.education}</span>}
          </div>
        </div>
        {/* Depth Score */}
        {depthScore && (
          <div className="flex items-center space-x-2 mt-2" data-testid="depth-score">
            <Award className={`w-4 h-4 ${DEPTH_COLORS[depthScore.level] || 'text-zinc-500'}`} />
            <span className={`text-xs font-bold ${DEPTH_COLORS[depthScore.level] || 'text-zinc-500'}`}>{depthScore.level}</span>
            <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-orange-400 rounded-full transition-all" style={{ width: `${depthScore.score}%` }} />
            </div>
            <span className="text-[10px] text-zinc-500">{depthScore.score}/100</span>
          </div>
        )}
        {/* Orbit Insights — own profile only */}
        {isOwn && insights.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="orbit-insights">
            {insights.slice(0, 3).map((ins, i) => {
              const INSIGHT_ICONS = { 'search': SearchIcon, 'users': Users, 'trending-up': TrendingUp, 'dollar-sign': DollarSign };
              const Icon = INSIGHT_ICONS[ins.icon] || Eye;
              return (
                <div key={i} className="flex items-center space-x-2.5 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
                  <div className="w-7 h-7 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-orange-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-white truncate">{ins.title}</p>
                    <p className="text-[9px] text-zinc-500 truncate">{ins.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Profile Completeness Ring — own profile */}
        {isOwn && <ProfileCompletenessRing user={displayUser} />}
        {/* Verification Section — own profile */}
        {isOwn && <VerificationBadgeSection user={displayUser} token={token} showToast={showToast} refreshUser={refreshUser} />}
        {/* Actions — Redesigned Buttons */}
        <div className="flex items-center space-x-2 mt-3">
          {isOwn ? (
            <>
              <button onClick={() => setShowScheduleModal(true)} className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 text-xs font-bold hover:bg-zinc-700 transition-colors flex items-center justify-center gap-1.5" data-testid="schedule-btn">
                <Calendar className="w-3.5 h-3.5" /> Schedule
              </button>
              <button onClick={() => setShowMonetization(true)} className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl py-2.5 text-xs font-bold hover:bg-zinc-700 transition-colors flex items-center justify-center gap-1.5" data-testid="monetization-btn">
                <span>₹</span> Pricing
              </button>
              <button onClick={handleShare} className="bg-zinc-800 border border-zinc-700 rounded-xl p-2.5 hover:bg-zinc-700 transition-colors" data-testid="share-profile-btn">
                <ExternalLink className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <ConnectButton userId={displayUser?.user_id} large />
              <JoinCommunityButton userId={displayUser?.user_id} showToast={showToast} />
              <button onClick={handleShare} className="bg-zinc-800 border border-zinc-700 rounded-xl p-2.5 hover:bg-zinc-700 transition-colors" data-testid="share-other-btn">
                <ExternalLink className="w-4 h-4" />
              </button>
              <div className="relative">
                <button onClick={() => setShowDotMenu(!showDotMenu)} className="bg-zinc-800 border border-zinc-700 rounded-xl p-2.5 hover:bg-zinc-700 transition-colors" data-testid="three-dot-menu">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {showDotMenu && (
                  <div className="absolute right-0 top-12 z-30 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl py-1 w-44" data-testid="dot-menu-dropdown">
                    <button onClick={async () => {
                      try {
                        const r = await fetch(`${API_URL}/api/favorites/${displayUser?.user_id}`, {
                          method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                        if (r.ok) {
                          const d = await r.json();
                          showToast?.(d.saved ? 'Profile saved!' : 'Profile unsaved');
                        }
                        setShowDotMenu(false);
                      } catch { showToast?.('Failed'); }
                    }} className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-zinc-800 flex items-center gap-2" data-testid="save-profile-btn">
                      <BookmarkIcon className="w-4 h-4" /> Save Profile
                    </button>
                    <button onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/u/@${displayUser?.username}`);
                      showToast?.('Profile link copied!'); setShowDotMenu(false);
                    }} className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-zinc-800 flex items-center gap-2" data-testid="copy-profile-link-btn">
                      <Copy className="w-4 h-4" /> Copy Link
                    </button>
                    <button onClick={async () => {
                      try {
                        await fetch(`${API_URL}/api/block/${displayUser?.user_id}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
                        showToast?.('User blocked'); setShowDotMenu(false);
                      } catch {}
                    }} className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-zinc-800 flex items-center gap-2" data-testid="block-person-btn">
                      <Shield className="w-4 h-4" /> Block Person
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Call Booking — full-width row, opens BookingFlowModal with date/note/payment */}
        {!isOwn && (
          <button onClick={() => setShowBookingFlow(true)}
            className="w-full mt-2 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 rounded-xl py-3 text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20"
            data-testid="call-booking-btn">
            <Calendar className="w-4 h-4" /> Call Booking
          </button>
        )}
      </div>

      {/* Skills & Expertise */}
      <div className="px-4 pb-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <h3 className="text-base font-extrabold text-white">Skills</h3>
            <span className="text-[10px] text-zinc-600 bg-zinc-900 px-1.5 py-0.5 rounded-md">{branchCount}/{MAX_BRANCHES}</span>
          </div>
          {isOwn && (
            <div className="flex items-center space-x-2">
              <button onClick={() => setShowVerifyQueue(true)} className="text-[10px] text-yellow-400 hover:underline font-medium" data-testid="peer-review-btn">Review</button>
              <button onClick={() => setShowAddExpertise(true)} className="text-xs text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 px-3 py-1.5 rounded-lg font-bold transition-colors flex items-center gap-1" data-testid="add-expertise-btn" disabled={branchCount >= MAX_BRANCHES}>
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          )}
        </div>
        {branchCount === 0 ? (
          <div className="py-4">
            {isOwn ? (
              <button onClick={() => setShowAddExpertise(true)}
                className="w-full py-4 border-2 border-dashed border-zinc-700 rounded-2xl text-sm text-zinc-400 hover:text-white hover:border-purple-500 transition-all font-medium"
                data-testid="add-first-expertise">
                <Plus className="w-5 h-5 mx-auto mb-1" /> Add your first skill
              </button>
            ) : <p className="text-xs text-zinc-600 py-2">No skills added yet</p>}
          </div>
        ) : (
          <div className="space-y-2.5">
            {(displayUser?.expertise_tree || []).map((b, i) => {
              const industryColors = {
                'Technology': 'from-blue-500/20 to-blue-600/5 border-blue-500/20',
                'Finance': 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/20',
                'Health': 'from-red-500/20 to-red-600/5 border-red-500/20',
                'Education': 'from-yellow-500/20 to-yellow-600/5 border-yellow-500/20',
                'Arts': 'from-pink-500/20 to-pink-600/5 border-pink-500/20',
                'Business': 'from-purple-500/20 to-purple-600/5 border-purple-500/20',
              };
              const colorClass = Object.entries(industryColors).find(([k]) => b.industry?.toLowerCase().includes(k.toLowerCase()))?.[1] || 'from-zinc-800/50 to-zinc-900 border-zinc-700/50';
              return (
                <div key={b.branch_id || i} className={`relative overflow-hidden rounded-2xl border bg-gradient-to-r transition-all hover:shadow-lg ${b.peer_verified ? 'border-yellow-500/40 from-yellow-500/10 to-transparent shadow-yellow-500/10' : b.verified ? 'border-yellow-500/25 from-yellow-500/5 to-transparent' : colorClass}`} data-testid={`expertise-${b.branch_id || i}`}>
                  <div className="px-4 py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-xl font-black text-white leading-tight">{b.expertise}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold bg-zinc-800/80 px-2 py-0.5 rounded-md">{b.industry}</span>
                          {b.subcategory && <span className="text-[10px] text-zinc-500 bg-zinc-800/60 px-2 py-0.5 rounded-md">{b.subcategory}</span>}
                          {b.microniche && <span className="text-[10px] text-zinc-500 bg-zinc-800/60 px-2 py-0.5 rounded-md">{b.microniche}</span>}
                          {b.peer_verified && <Shield className="w-3.5 h-3.5 text-yellow-400" />}
                          {b.verified && !b.peer_verified && <Check className="w-3.5 h-3.5 text-yellow-500" />}
                        </div>
                      </div>
                      <span className={`text-[10px] px-2.5 py-1 rounded-lg font-bold shrink-0 ${SKILL_LEVELS.find(s => s.value === b.skill_level)?.color || 'bg-zinc-800 text-zinc-400'}`}>
                        {(b.skill_level || 'intermediate').charAt(0).toUpperCase() + (b.skill_level || 'intermediate').slice(1)}
                      </span>
                    </div>
                    {isOwn && (
                      <div className="flex items-center space-x-2 mt-2.5 pt-2 border-t border-white/5">
                        <select value={b.skill_level || 'intermediate'} onChange={e => updateSkillLevel(b.branch_id, e.target.value)}
                          className="bg-zinc-800/80 text-[11px] rounded-lg px-2 py-1.5 outline-none border border-zinc-700/50 text-zinc-300" data-testid={`level-${b.branch_id}`}>
                          {SKILL_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                        <button onClick={() => removeBranch(b.branch_id)} className="text-zinc-600 hover:text-red-400 transition-colors ml-auto"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Monetization Quick View (other profiles) — Book Session + Group Sessions */}
      {!isOwn && (
        <div className="px-4 py-3 border-b border-zinc-800" data-testid="price-tags-section">
          <p className="text-xs text-zinc-500 font-semibold mb-2 uppercase tracking-wide">Book a Session</p>
          <div className="grid grid-cols-2 gap-2">
            {displayUser?.monetization_dm !== false && <PriceTag label="DM" price={displayUser?.price_dm || 5} icon={MessageSquare} onClick={() => handlePriceTagClick('dm')} />}
            {displayUser?.monetization_voice_note !== false && <PriceTag label="Voice Note" price={displayUser?.price_voice_note || 10} icon={Phone} onClick={() => handlePriceTagClick('voice_note')} />}
            {displayUser?.monetization_voice_call !== false && <PriceTag label="Voice Call" price={displayUser?.price_voice_call || 50} icon={Phone} onClick={() => handlePriceTagClick('voice_call')} />}
            {displayUser?.monetization_video_call !== false && <PriceTag label="Video Call" price={displayUser?.price_video_call || 75} icon={Video} onClick={() => handlePriceTagClick('video_call')} />}
          </div>
          {paymentLoading && <p className="text-xs text-zinc-500 mt-1 animate-pulse">Processing payment...</p>}

          {/* Group Sessions — full-width bar cards */}
          {userGroupEvents.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="group-sessions-list">
              <p className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">Group Sessions</p>
              {userGroupEvents.map(evt => {
                const attendees = evt.attendees || [];
                const isJoined = attendees.some(a => (typeof a === 'string' ? a : a.user_id) === user?.user_id);
                const isFull = attendees.length >= (evt.max_participants || 20);
                const formatDate = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return d; } };
                const formatTime = (t) => { if (!t) return ''; const [h, m] = t.split(':').map(Number); const ampm = h >= 12 ? 'PM' : 'AM'; return `${h % 12 || 12}${m ? ':' + String(m).padStart(2, '0') : ''} ${ampm}`; };
                const timeStr = evt.time_to ? `${formatTime(evt.time)} - ${formatTime(evt.time_to)}` : formatTime(evt.time);
                return (
                  <button key={evt.event_id}
                    onClick={() => !isJoined && !isFull && handleJoinGroupEvent(evt)}
                    disabled={isJoined || isFull || joiningEvent === evt.event_id}
                    className={`w-full rounded-xl border transition-all text-left overflow-hidden ${isJoined ? 'bg-green-500/10 border-green-500/30' : isFull ? 'bg-zinc-800/50 border-zinc-700 opacity-60' : 'bg-zinc-800 border-zinc-700 hover:border-purple-500/50 hover:bg-zinc-800/80'}`}
                    data-testid={`group-event-${evt.event_id}`}>
                    {evt.cover_image && (
                      <div className="h-20 w-full bg-zinc-900 overflow-hidden">
                        <img src={`${API_URL}/api/storage/${evt.cover_image}`} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="px-3 py-2.5 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{evt.topic || evt.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-zinc-400">{formatDate(evt.date)} · {timeStr}</span>
                          <span className="text-[10px] text-zinc-600">·</span>
                          <span className="text-[10px] text-zinc-400">{attendees.length}/{evt.max_participants || 20} joined</span>
                        </div>
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        {isJoined ? (
                          <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-1 rounded-lg font-bold">Joined</span>
                        ) : isFull ? (
                          <span className="text-[10px] text-zinc-500 font-bold">Full</span>
                        ) : (
                          <span className="text-xs font-bold text-white bg-purple-600 px-3 py-1 rounded-lg">
                            {joiningEvent === evt.event_id ? '...' : evt.price > 0 ? `₹${evt.price}` : 'Free'}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Wallet & Referral Section — own profile only */}
      {isOwn && (
        <div className="px-4 py-3 border-t border-zinc-800 space-y-3" data-testid="wallet-section">
          {/* Wallet Card */}
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm font-bold text-white">Wallet</span>
              </div>
              <button onClick={() => setShowWallet(!showWallet)} className="text-xs text-zinc-400 hover:text-white">
                {showWallet ? 'Hide' : 'Details'}
              </button>
            </div>
            <p className="text-3xl font-black text-white mb-1">₹{walletData?.balance?.toFixed(2) || '0.00'}</p>
            <div className="flex items-center gap-4 text-[10px] text-zinc-500">
              <button onClick={() => setShowEarningsDetail(!showEarningsDetail)} className="flex items-center gap-1 hover:text-emerald-400 transition-colors cursor-pointer">
                <ArrowDownLeft className="w-3 h-3 text-green-400" />Earned: ₹{walletData?.total_earnings?.toFixed(2) || '0.00'}
              </button>
              <button onClick={() => setShowSpentDetail(!showSpentDetail)} className="flex items-center gap-1 hover:text-orange-400 transition-colors cursor-pointer">
                <ArrowUpRight className="w-3 h-3 text-orange-400" />Spent: ₹{walletData?.total_spent?.toFixed(2) || '0.00'}
              </button>
            </div>
            {/* Earnings Detail Dropdown */}
            {showEarningsDetail && (
              <div className="mt-2 bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 space-y-1.5" data-testid="earnings-detail">
                <p className="text-[10px] text-zinc-400 font-semibold mb-1 uppercase tracking-wide">Earnings Breakdown</p>
                {(walletData?.transactions || []).filter(t => t.type === 'credit' || t.amount > 0).slice(0, 8).map((t, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[11px] text-zinc-400 truncate flex-1">{t.description}</span>
                    <span className="text-[11px] text-emerald-400 font-bold ml-2">+₹{Math.abs(t.amount).toFixed(2)}</span>
                  </div>
                ))}
                {(!walletData?.transactions || walletData.transactions.filter(t => t.amount > 0).length === 0) &&
                  <p className="text-[10px] text-zinc-600">No earnings yet</p>}
              </div>
            )}
            {/* Spent Detail Dropdown */}
            {showSpentDetail && (
              <div className="mt-2 bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 space-y-1.5" data-testid="spent-detail">
                <p className="text-[10px] text-zinc-400 font-semibold mb-1 uppercase tracking-wide">Spending History</p>
                {(walletData?.transactions || []).filter(t => t.type === 'debit' || t.amount < 0).slice(0, 8).map((t, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-[11px] text-zinc-400 truncate flex-1">{t.description}</span>
                    <span className="text-[11px] text-orange-400 font-bold ml-2">-₹{Math.abs(t.amount).toFixed(2)}</span>
                  </div>
                ))}
                {(!walletData?.transactions || walletData.transactions.filter(t => t.amount < 0).length === 0) &&
                  <p className="text-[10px] text-zinc-600">No spending yet</p>}
              </div>
            )}

            {showWallet && (
              <div className="mt-4 space-y-3">
                {/* Top-up */}
                <div>
                  <p className="text-xs text-zinc-400 mb-2 font-medium">Add Funds</p>
                  <div className="flex gap-2 mb-2">
                    {[100, 250, 500, 1000].map(amt => (
                      <button key={amt} onClick={() => setTopUpAmount(amt)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${topUpAmount === amt ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                        data-testid={`topup-${amt}`}>
                        ₹{amt}
                      </button>
                    ))}
                  </div>
                  <button onClick={topUpWallet} disabled={topUpLoading}
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-sm transition-colors disabled:opacity-50"
                    data-testid="topup-btn">
                    {topUpLoading ? 'Processing...' : `Top Up ₹${topUpAmount}`}
                  </button>
                </div>

                {/* QR Code */}
                <button onClick={fetchQr} className="w-full flex items-center justify-center gap-2 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium transition-colors" data-testid="show-qr-btn">
                  <QrCode className="w-4 h-4" /> Show Payment QR Code
                </button>

                {/* Referral earnings */}
                {walletData?.referral_earnings > 0 && (
                  <div className="flex items-center justify-between bg-zinc-800 rounded-xl px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-zinc-300">Referral Earnings</span>
                    </div>
                    <span className="text-sm font-bold text-purple-400">₹{walletData.referral_earnings.toFixed(2)}</span>
                  </div>
                )}

                {/* Recent wallet transactions */}
                {walletData?.transactions?.length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium">Recent</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {walletData.transactions.slice(0, 5).map((t, i) => (
                        <div key={t.txn_id || i} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            {t.amount > 0 ? <ArrowDownLeft className="w-3 h-3 text-green-400" /> : <ArrowUpRight className="w-3 h-3 text-orange-400" />}
                            <span className="text-[11px] text-zinc-300 truncate max-w-[160px]">{t.description}</span>
                          </div>
                          <span className={`text-xs font-bold ${t.amount > 0 ? 'text-green-400' : 'text-orange-400'}`}>
                            {t.amount > 0 ? '+' : ''}₹{Math.abs(t.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ Profile Sub-Tabs ═══ */}
      <ProfileSubTabs isOwn={isOwn} user={user} displayUser={displayUser} posts={posts} showToast={showToast} onProfileClick={onProfileClick} onSwitchTab={onSwitchTab} />

      {/* ═══ Certificates Display + Upload ═══ */}
      <div className="px-4 py-3 border-t border-zinc-800" id="certificates-section" data-testid="certificates-section">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">Certificates & Credentials</p>
          {isOwn && (
            <label className="flex items-center gap-1 text-xs text-purple-400 cursor-pointer hover:text-purple-300 bg-purple-500/10 px-3 py-1.5 rounded-lg font-medium">
              <Plus className="w-3.5 h-3.5" /> Add
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('file', file);
                formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
                try {
                  const res = await fetch(`${API_URL}/api/certificates`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
                  if (res.ok) { showToast?.('Certificate uploaded!'); refreshUser?.(); }
                } catch {}
              }} data-testid="cert-upload-input" />
            </label>
          )}
        </div>
        {(displayUser?.certificates?.length > 0) ? (
          <div className="grid grid-cols-2 gap-2">
            {displayUser.certificates.map((c, i) => (
              <div key={c.cert_id || i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-zinc-600 transition-colors group relative" data-testid={`cert-${i}`}>
                <a href={c.file_url?.startsWith('http') ? c.file_url : (c.certificate_path?.startsWith('http') ? c.certificate_path : `${API_URL}/api/storage/${c.file_url || c.certificate_path}`)}
                  target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-zinc-300 truncate block">{c.name || c.title || `Certificate ${i + 1}`}</span>
                    {c.issuer && <span className="text-[9px] text-zinc-500 truncate block">{c.issuer}</span>}
                  </div>
                  {c.verified && <Shield className="w-3 h-3 text-green-400 shrink-0" />}
                </a>
                {isOwn && c.cert_id && (
                  <button onClick={async () => {
                    await fetch(`${API_URL}/api/certificates/${c.cert_id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                    showToast?.('Certificate deleted');
                    refreshUser?.();
                  }} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-red-500/20 rounded-full transition-opacity" data-testid={`cert-delete-${i}`}>
                    <X className="w-3 h-3 text-red-400" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-zinc-600">{isOwn ? 'Upload certificates to build trust' : 'No certificates yet'}</p>
        )}
      </div>

      {/* ═══ Enriched Profile Fields ═══ */}
      <EnrichedProfileSection displayUser={displayUser} isOwn={isOwn} headers={{ 'Authorization': `Bearer ${token}` }} showToast={showToast} onRefresh={() => refreshUser?.()} />


      {/* ═══ Referral Section — at bottom ═══ */}
      {isOwn && (
        <div className="px-4 py-3 border-t border-zinc-800 space-y-3" data-testid="referral-bottom-section">
          {!user?.referred_by && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="referral-section">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-bold text-white">Got a Referral Code?</span>
              </div>
              <p className="text-[11px] text-zinc-500 mb-3">Apply a referral code to unlock exclusive bundle deals</p>
              <div className="flex gap-2">
                <input type="text" value={referralInput} onChange={e => setReferralInput(e.target.value)}
                  placeholder="Enter code (e.g. ref_abc123)" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-purple-500/50 placeholder-zinc-600"
                  data-testid="referral-input" />
                <button onClick={applyReferral} disabled={referralLoading || !referralInput.trim()}
                  className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl text-sm disabled:opacity-40 transition-colors"
                  data-testid="apply-referral-btn">
                  {referralLoading ? '...' : 'Apply'}
                </button>
              </div>
            </div>
          )}
          {user?.referral_code && <ReferralDashboard user={user} headers={headers} showToast={showToast} onProfileClick={onProfileClick} />}
        </div>
      )}

      {/* ═══ In-App Suggestion Box ═══ */}
      {isOwn && (
        <div className="px-4 py-3 border-t border-zinc-800" data-testid="suggestion-section-inapp">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <p className="text-xs text-zinc-400 font-semibold mb-2">Have a suggestion?</p>
            {suggestionSent ? (
              <p className="text-xs text-emerald-400">Thanks! We read every suggestion.</p>
            ) : (
              <div className="flex gap-2">
                <input type="text" value={suggestionText} onChange={e => setSuggestionText(e.target.value)}
                  placeholder="What would make Connect better?" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none placeholder-zinc-600"
                  data-testid="suggestion-input-inapp" />
                <button onClick={async () => {
                  if (!suggestionText.trim()) return;
                  await fetch(`${API_URL}/api/suggestions`, {
                    method: 'POST', headers,
                    body: JSON.stringify({ message: suggestionText, category: 'in-app' }),
                  });
                  setSuggestionText(''); setSuggestionSent(true);
                }} disabled={!suggestionText.trim()} className="px-4 py-2 bg-white text-black font-bold rounded-xl text-sm disabled:opacity-30"
                  data-testid="suggestion-submit-inapp">Send</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showSettings && <SettingsModal user={user} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); refreshUser?.(); }} calendarLinked={calendarLinked} onLinkCalendar={linkCalendar} onUnlinkCalendar={unlinkCalendar} headers={headers} showToast={showToast} onProfileClick={onProfileClick} />}
      {showScheduleModal && <ScheduleModal user={user} displayUser={displayUser} onClose={() => setShowScheduleModal(false)} headers={headers} showToast={showToast} refreshUser={refreshUser} />}
      {showMonetization && <MonetizationModal user={user} onClose={() => setShowMonetization(false)} onSaved={() => { setShowMonetization(false); refreshUser?.(); }} />}
      {showBookingFlow && !isOwn && <BookingFlowModal expert={displayUser} user={user} initialCallType={bookingCallType} onClose={() => setShowBookingFlow(false)} showToast={showToast} />}
      {showAddExpertise && <AddExpertiseModal onClose={() => setShowAddExpertise(false)} onAdded={() => { setShowAddExpertise(false); refreshUser?.(); fetchDepthScore(); }} />}
      {showVerifyQueue && <PeerVerifyModal onClose={() => setShowVerifyQueue(false)} />}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end justify-center" onClick={() => setShowShareModal(false)} data-testid="share-modal">
          <div className="bg-zinc-900 border-t border-zinc-700 rounded-t-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="w-8 h-1 bg-zinc-700 rounded-full mx-auto" />
            <h3 className="text-sm font-bold text-white text-center">Share Profile</h3>
            <div className="bg-zinc-800 rounded-xl p-3 flex items-center space-x-3">
              <Link2 className="w-5 h-5 text-zinc-500 shrink-0" />
              <p className="flex-1 text-xs text-zinc-300 truncate">{shareLink}</p>
              <button onClick={copyLink} className="text-white bg-zinc-700 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-zinc-600 flex items-center space-x-1" data-testid="copy-share-link">
                <Copy className="w-3 h-3" /><span>Copy</span>
              </button>
            </div>
            <p className="text-[10px] text-zinc-500 text-center">When shared on social media, this link shows your name, expertise, and rating as a rich preview card.</p>
            <div className="flex items-center space-x-2">
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(`Check out my profile on Connect!`)}`}
                target="_blank" rel="noreferrer" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg py-2.5 text-xs font-semibold text-center hover:bg-zinc-700">Twitter</a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`}
                target="_blank" rel="noreferrer" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg py-2.5 text-xs font-semibold text-center hover:bg-zinc-700">LinkedIn</a>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Check out my profile: ${shareLink}`)}`}
                target="_blank" rel="noreferrer" className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg py-2.5 text-xs font-semibold text-center hover:bg-zinc-700">WhatsApp</a>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQr && qrData && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)} data-testid="qr-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-sm w-full mx-4 text-center" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-white mb-1">Payment QR Code</h3>
            <p className="text-xs text-zinc-500 mb-4">Scan to pay for a {qrData.payment_type?.replace('_', ' ')}</p>
            <div className="bg-white rounded-xl p-4 inline-block mb-4">
              <img src={qrData.qr_base64} alt="QR Code" className="w-48 h-48" />
            </div>
            <p className="text-[10px] text-zinc-500 break-all mb-3">{qrData.pay_url}</p>
            <button onClick={() => setShowQr(false)} className="w-full py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium">Close</button>
          </div>
        </div>
      )}
      {/* Photo Modal */}
      {showPhotoModal && displayUser?.picture && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setShowPhotoModal(false)} data-testid="photo-modal">
          <img src={`${API_URL}${displayUser.picture}`} alt="" className="max-w-[90vw] max-h-[80vh] rounded-2xl object-contain" />
          <button onClick={() => setShowPhotoModal(false)} className="absolute top-4 right-4 w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
      )}

      {/* Followers/Connected List Modal */}
      {showFollowersList && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShowFollowersList(null)} data-testid="followers-modal">
          <div className="bg-zinc-900 w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-zinc-800 max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <h3 className="font-bold text-sm capitalize">{showFollowersList}</h3>
              <button onClick={() => setShowFollowersList(null)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="px-3 pt-2 pb-1">
              <div className="relative">
                <SearchIcon className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" placeholder="Search..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl pl-9 pr-3 py-2 text-xs outline-none text-white placeholder-zinc-600"
                  onChange={e => { const q = e.target.value.toLowerCase(); document.querySelectorAll('[data-followers-item]').forEach(el => { el.style.display = el.dataset.followersItem.toLowerCase().includes(q) ? '' : 'none'; }); }}
                  data-testid="followers-search" />
              </div>
            </div>
            <div className="overflow-y-auto max-h-[55vh] p-2">
              {followersList.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No users yet</p>
              ) : followersList.map((u, i) => (
                <div key={u.user_id || i} data-followers-item={u.name || ''} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-zinc-800 cursor-pointer transition-colors"
                  onClick={() => { setShowFollowersList(null); onProfileClick?.(u.user_id); }}>
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-sm font-bold shrink-0">
                    {u.picture ? <img src={u.picture.startsWith('http') ? u.picture : `${API_URL}${u.picture}`} alt="" className="w-full h-full object-cover" /> : u.name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{u.name}</p>
                    {u.headline && <p className="text-[10px] text-zinc-500 truncate">{u.headline}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function ProfileSubTabs({ isOwn, user, displayUser, posts, showToast, onProfileClick, onSwitchTab }) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  return (
    <div className="pb-20" data-testid="profile-subtabs">
      {/* Posts Header */}
      <div className="flex border-b border-zinc-800 sticky top-0 z-10 bg-black">
        <div className="flex-1 py-2.5 flex items-center justify-center gap-1.5 border-b-2 border-white text-white" data-testid="subtab-posts">
          <Grid className="w-4 h-4" />
          <span className="text-[11px] font-medium">Posts</span>
        </div>
      </div>
      <PostsGrid isOwn={isOwn} posts={posts} onSwitchTab={onSwitchTab} />
    </div>
  );
}

function PostsGrid({ isOwn, posts, onSwitchTab }) {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const [postsState, setPostsState] = useState(posts);
  const [saved, setSaved] = useState({});
  const [openComments, setOpenComments] = useState({});  // postId → array of comments
  const [commentText, setCommentText] = useState({});    // postId → input text
  const [commentLoading, setCommentLoading] = useState({});
  useEffect(() => { setPostsState(posts); }, [posts]);

  const toggleLike = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/like`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        setPostsState(prev => prev.map(p => p.post_id === postId ?
          { ...p, likes_count: (p.likes_count || 0) + (d.liked ? 1 : -1), user_liked: d.liked } : p));
      }
    } catch {}
  };
  const toggleSave = async (postId) => {
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/save`, { method: 'POST', headers });
      if (res.ok) {
        const d = await res.json();
        setSaved(s => ({ ...s, [postId]: d.saved }));
      }
    } catch {}
  };
  const share = async (p) => {
    const url = `${window.location.origin}/app?post=${p.post_id}`;
    try {
      if (navigator.share) await navigator.share({ title: p.user_name, text: p.content?.substring(0, 100), url });
      else { navigator.clipboard.writeText(url); }
    } catch {}
  };

  const toggleComments = async (postId) => {
    if (openComments[postId] !== undefined) {
      // Already loaded — just toggle close by removing
      setOpenComments(prev => { const n = { ...prev }; delete n[postId]; return n; });
      return;
    }
    setCommentLoading(l => ({ ...l, [postId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comments`, { headers });
      if (res.ok) {
        const list = await res.json();
        setOpenComments(prev => ({ ...prev, [postId]: list }));
      } else {
        setOpenComments(prev => ({ ...prev, [postId]: [] }));
      }
    } catch {
      setOpenComments(prev => ({ ...prev, [postId]: [] }));
    }
    setCommentLoading(l => ({ ...l, [postId]: false }));
  };

  const submitComment = async (postId) => {
    const text = (commentText[postId] || '').trim();
    if (!text) return;
    try {
      const res = await fetch(`${API_URL}/api/posts/${postId}/comment?text=${encodeURIComponent(text)}`,
        { method: 'POST', headers });
      if (res.ok) {
        const newComment = await res.json();
        setOpenComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), newComment] }));
        setCommentText(c => ({ ...c, [postId]: '' }));
        setPostsState(prev => prev.map(p => p.post_id === postId ?
          { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      }
    } catch {}
  };

  return (
    <div className="space-y-0" data-testid="posts-grid">
      {isOwn && (
        <button onClick={() => { if (onSwitchTab) { onSwitchTab('feed'); setTimeout(() => window.dispatchEvent(new CustomEvent('open-new-post')), 200); } else { window.dispatchEvent(new CustomEvent('open-new-post')); } }}
          className="w-full py-4 border-b border-zinc-800 text-center hover:bg-zinc-900/50 transition-colors" data-testid="profile-new-post">
          <Plus className="w-6 h-6 text-zinc-600 mx-auto mb-1" />
          <span className="text-xs text-zinc-600">New Post</span>
        </button>
      )}
      {postsState.map(p => (
        <div key={p.post_id} className="border-b border-zinc-800 px-4 py-3" data-testid={`profile-post-${p.post_id}`}>
          {p.media_path ? (
            <div className="rounded-xl overflow-hidden mb-2">
              <img src={`${API_URL}/api/storage/${p.media_path}`} alt="" className="w-full aspect-video object-cover" />
            </div>
          ) : null}
          {p.content && <p className="text-sm text-zinc-200 leading-relaxed">{p.content}</p>}
          {/* Action buttons */}
          <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-zinc-800/60">
            <div className="flex items-center gap-5">
              {(p.owner_privacy?.like !== false) && (
                <button onClick={() => toggleLike(p.post_id)} className="flex items-center gap-1.5 text-zinc-300 hover:text-red-400 transition-colors" data-testid={`profile-like-${p.post_id}`}>
                  <Heart className={`w-6 h-6 ${p.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
                  <span className="text-xs font-semibold">{p.likes_count || 0}</span>
                </button>
              )}
              {(p.owner_privacy?.comment !== false) && (
                <button onClick={() => toggleComments(p.post_id)} className="flex items-center gap-1.5 text-zinc-300 hover:text-white transition-colors" data-testid={`profile-comment-${p.post_id}`}>
                  <MessageSquare className="w-6 h-6" />
                  <span className="text-xs font-semibold">{p.comments_count || 0}</span>
                </button>
              )}
              {(p.owner_privacy?.share !== false) && (
                <button onClick={() => share(p)} className="text-zinc-300 hover:text-white transition-colors" data-testid={`profile-share-${p.post_id}`}>
                  <Share2 className="w-6 h-6" />
                </button>
              )}
            </div>
            {(p.owner_privacy?.post_save !== false) && (
              <button onClick={() => toggleSave(p.post_id)} className={`transition-colors ${saved[p.post_id] ? 'text-white' : 'text-zinc-300 hover:text-white'}`} data-testid={`profile-save-${p.post_id}`}>
                <BookmarkIcon className={`w-6 h-6 ${saved[p.post_id] ? 'fill-white' : ''}`} />
              </button>
            )}
          </div>
          <p className="text-[10px] text-zinc-600 mt-2">{new Date(p.created_at).toLocaleDateString()}</p>

          {/* Inline Comments */}
          {openComments[p.post_id] !== undefined && (
            <div className="mt-3 pt-3 border-t border-zinc-800/60 space-y-2.5" data-testid={`comments-${p.post_id}`}>
              {commentLoading[p.post_id] && <p className="text-[10px] text-zinc-500 text-center py-2">Loading...</p>}
              {!commentLoading[p.post_id] && openComments[p.post_id].length === 0 && (
                <p className="text-[10px] text-zinc-600 text-center py-1">No comments yet — be the first</p>
              )}
              {openComments[p.post_id].map(c => (
                <div key={c.comment_id} className="flex items-start gap-2" data-testid={`comment-${c.comment_id}`}>
                  <div className="w-6 h-6 rounded-full bg-zinc-800 overflow-hidden shrink-0 mt-0.5">
                    {c.user_picture ? <img src={c.user_picture.startsWith('http') ? c.user_picture : `${API_URL}${c.user_picture}`} alt="" className="w-full h-full object-cover" /> :
                      <span className="w-full h-full flex items-center justify-center text-[9px] font-bold text-white">{c.user_name?.[0]}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] leading-snug">
                      <span className="font-semibold text-white mr-1">{c.user_name}</span>
                      <span className="text-zinc-300">{c.text}</span>
                    </p>
                    <p className="text-[9px] text-zinc-600 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</p>
                  </div>
                </div>
              ))}
              {/* Compose */}
              <div className="flex items-center gap-2 pt-1">
                <input type="text" value={commentText[p.post_id] || ''}
                  onChange={e => setCommentText(prev => ({ ...prev, [p.post_id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') submitComment(p.post_id); }}
                  placeholder="Add a comment..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 text-[11px] outline-none focus:border-zinc-600 text-white"
                  data-testid={`comment-input-${p.post_id}`} />
                <button onClick={() => submitComment(p.post_id)} disabled={!(commentText[p.post_id] || '').trim()}
                  className="px-3 py-1.5 bg-white text-black text-[10px] font-bold rounded-full disabled:opacity-30"
                  data-testid={`comment-submit-${p.post_id}`}>Post</button>
              </div>
            </div>
          )}
        </div>
      ))}
      {postsState.length === 0 && !isOwn && <p className="text-center text-xs text-zinc-600 py-8">No posts yet</p>}
    </div>
  );
}

/* ──── Analytics Dashboard ──── */
function AnalyticsDashboard({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/analytics/dashboard`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-center py-12 text-zinc-600 text-sm">Could not load analytics</div>;

  const maxBar = Math.max(...(data.earnings?.monthly || []).map(m => m.amount), 1);
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const maxDay = Math.max(...(data.busiest_days || []).map(d => d.count), 1);

  return (
    <div className="px-4 py-4 space-y-5" data-testid="analytics-dashboard">
      {/* Earnings Cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <p className="text-[10px] text-zinc-500 mb-1">Total Earned</p>
          <p className="text-lg font-black text-white">₹{data.earnings?.total?.toFixed(2)}</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 text-center">
          <p className="text-[10px] text-zinc-500 mb-1">You Received</p>
          <p className="text-lg font-black text-emerald-400">₹{data.earnings?.net?.toFixed(2)}</p>
        </div>
      </div>

      {/* Monthly Earnings Bar Chart */}
      {data.earnings?.monthly?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="monthly-chart">
          <h4 className="text-xs font-bold text-white mb-3">Monthly Earnings</h4>
          <div className="flex items-end gap-2 h-28">
            {data.earnings.monthly.map(m => (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                <div className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-lg transition-all" style={{ height: `${Math.max((m.amount / maxBar) * 100, 4)}%` }} />
                <p className="text-[9px] text-zinc-500 mt-1">{m.month.split('-')[1]}/{m.month.split('-')[0].slice(2)}</p>
                <p className="text-[9px] text-zinc-400 font-bold">₹{m.amount}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bookings Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <p className="text-[10px] text-zinc-500 mb-2">Bookings</p>
          <div className="space-y-1.5">
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Total</span><span className="text-sm font-bold text-white">{data.bookings?.total || 0}</span></div>
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Completed</span><span className="text-sm font-bold text-emerald-400">{data.bookings?.completed || 0}</span></div>
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Upcoming</span><span className="text-sm font-bold text-blue-400">{data.bookings?.upcoming || 0}</span></div>
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Cancelled</span><span className="text-sm font-bold text-red-400">{data.bookings?.cancelled || 0}</span></div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
          <p className="text-[10px] text-zinc-500 mb-2">Clients</p>
          <div className="space-y-1.5">
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Unique</span><span className="text-sm font-bold text-white">{data.clients?.unique || 0}</span></div>
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Repeat</span><span className="text-sm font-bold text-purple-400">{data.clients?.repeat || 0}</span></div>
            <div className="flex justify-between"><span className="text-[11px] text-zinc-400">Retention</span><span className="text-sm font-bold text-amber-400">{data.clients?.retention_rate || 0}%</span></div>
          </div>
        </div>
      </div>

      {/* Earnings By Type */}
      {Object.keys(data.earnings?.by_type || {}).length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="earnings-by-type">
          <h4 className="text-xs font-bold text-white mb-3">Earnings by Service</h4>
          <div className="space-y-2">
            {Object.entries(data.earnings.by_type).map(([type, amount]) => {
              const total = data.earnings.total || 1;
              const pct = Math.round((amount / total) * 100);
              const labels = { dm: 'DMs', voice_call: 'Voice Calls', video_call: 'Video Calls', voice_note: 'Voice Notes' };
              const colors = { dm: 'bg-blue-500', voice_call: 'bg-green-500', video_call: 'bg-purple-500', voice_note: 'bg-amber-500' };
              return (
                <div key={type}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-zinc-400">{labels[type] || type}</span>
                    <span className="text-white font-bold">₹{amount.toFixed(2)} ({pct}%)</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-1.5">
                    <div className={`${colors[type] || 'bg-zinc-500'} rounded-full h-1.5 transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Busiest Days */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="busiest-days">
        <h4 className="text-xs font-bold text-white mb-3">Busiest Days</h4>
        <div className="flex items-end gap-2 h-20">
          {(data.busiest_days || []).map(d => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full">
              <div className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t-lg transition-all" style={{ height: `${Math.max((d.count / maxDay) * 100, 4)}%` }} />
              <p className="text-[9px] text-zinc-500 mt-1">{d.day}</p>
              <p className="text-[9px] text-zinc-400">{d.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Popular Time Slots */}
      {data.popular_slots?.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4" data-testid="popular-slots">
          <h4 className="text-xs font-bold text-white mb-3">Most Popular Time Slots</h4>
          <div className="space-y-1.5">
            {data.popular_slots.map((s, i) => (
              <div key={s.time} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-600 w-4">{i + 1}.</span>
                  <span className="text-sm font-medium text-white">{s.time}</span>
                </div>
                <span className="text-xs text-zinc-500">{s.count} session{s.count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wallet Quick View */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
        <h4 className="text-xs font-bold text-white mb-3">Wallet</h4>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-black text-white">₹{data.wallet?.balance?.toFixed(2)}</p>
            <p className="text-[10px] text-zinc-500">Balance</p>
          </div>
          <div>
            <p className="text-lg font-black text-emerald-400">₹{data.wallet?.total_earnings?.toFixed(2)}</p>
            <p className="text-[10px] text-zinc-500">Total In</p>
          </div>
          <div>
            <p className="text-lg font-black text-orange-400">₹{data.wallet?.total_spent?.toFixed(2)}</p>
            <p className="text-[10px] text-zinc-500">Total Out</p>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ──── Calendar View ──── */
function CalendarView({ user, headers, showToast }) {
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [slots, setSlots] = useState([]);
  const [userTimezone, setUserTimezone] = useState('Asia/Kolkata');
  const [showTzPicker, setShowTzPicker] = useState(false);
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const HOURS_24 = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

  const ALL_TIMEZONES = [
    'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Karachi', 'Asia/Colombo', 'Asia/Kathmandu', 'Asia/Dubai', 'Asia/Tehran',
    'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Jakarta', 'Asia/Bangkok',
    'Asia/Manila', 'Asia/Taipei', 'Asia/Kuala_Lumpur', 'Asia/Riyadh', 'Asia/Baghdad', 'Asia/Beirut',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam',
    'Europe/Brussels', 'Europe/Vienna', 'Europe/Warsaw', 'Europe/Prague', 'Europe/Stockholm', 'Europe/Oslo',
    'Europe/Helsinki', 'Europe/Athens', 'Europe/Bucharest', 'Europe/Moscow', 'Europe/Istanbul', 'Europe/Dublin',
    'Europe/Lisbon', 'Europe/Zurich',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix',
    'America/Toronto', 'America/Vancouver', 'America/Mexico_City', 'America/Bogota', 'America/Lima',
    'America/Sao_Paulo', 'America/Buenos_Aires', 'America/Santiago', 'America/Caracas', 'America/Havana',
    'America/Anchorage', 'America/Honolulu',
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide',
    'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu', 'Pacific/Guam',
    'Africa/Cairo', 'Africa/Lagos', 'Africa/Johannesburg', 'Africa/Nairobi', 'Africa/Casablanca', 'Africa/Accra',
    'Atlantic/Reykjavik',
  ];

  const formatTo12h = (hour24) => {
    const h = parseInt(hour24.split(':')[0]);
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  useEffect(() => { fetchSchedule(); fetchTimezone(); }, []);

  const fetchTimezone = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/timezone/${user?.user_id}`);
      if (res.ok) { const d = await res.json(); setUserTimezone(d.timezone || 'Asia/Kolkata'); }
    } catch {}
  };

  const saveTimezone = async (tz) => {
    try {
      await fetch(`${API_URL}/api/calendar/timezone?tz=${encodeURIComponent(tz)}`, { method: 'POST', headers });
      setUserTimezone(tz);
      setShowTzPicker(false);
      if (showToast) showToast(`Timezone set to ${tz}`);
    } catch {}
  };

  const fetchSchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/my-schedule?days=30`, { headers });
      if (res.ok) {
        const d = await res.json();
        setBookings(d.bookings || []);
        setAvailability(d.availability || []);
        const initial = [];
        (d.availability || []).forEach(a => {
          initial.push({ day: a.day_of_week, start: a.start_time, end: a.end_time });
        });
        setSlots(initial);
      }
    } catch {}
    setLoading(false);
  };

  const toggleSlot = (day, hour) => {
    const exists = slots.find(s => s.day === day && s.start === hour);
    if (exists) {
      setSlots(prev => prev.filter(s => !(s.day === day && s.start === hour)));
    } else {
      const endH = parseInt(hour.split(':')[0]) + 1;
      setSlots(prev => [...prev, { day, start: hour, end: `${String(endH).padStart(2, '0')}:00` }]);
    }
  };

  const isSlotActive = (day, hour) => {
    return slots.some(s => {
      if (s.day !== day) return false;
      const sH = parseInt(s.start.split(':')[0]);
      const eH = parseInt(s.end.split(':')[0]);
      const h = parseInt(hour.split(':')[0]);
      return h >= sH && h < eH;
    });
  };

  const saveAvailability = async () => {
    const merged = {};
    slots.forEach(s => {
      if (!merged[s.day]) merged[s.day] = [];
      merged[s.day].push(s);
    });

    const apiSlots = [];
    Object.entries(merged).forEach(([day, daySlots]) => {
      const sorted = daySlots.sort((a, b) => a.start.localeCompare(b.start));
      let current = null;
      sorted.forEach(s => {
        if (!current) { current = { ...s }; return; }
        if (s.start === current.end) { current.end = s.end; }
        else { apiSlots.push(current); current = { ...s }; }
      });
      if (current) apiSlots.push(current);
    });

    try {
      const body = apiSlots.map(s => ({ day_of_week: s.day, start_time: s.start, end_time: s.end, is_active: true }));
      const res = await fetch(`${API_URL}/api/calendar/availability`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (res.ok) { showToast('Availability saved!'); setEditing(false); fetchSchedule(); }
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="px-4 py-4" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">My Schedule</h3>
        <button onClick={() => editing ? saveAvailability() : setEditing(true)}
          className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${editing ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
          data-testid="edit-availability-btn">
          {editing ? 'Save' : 'Edit Availability'}
        </button>
      </div>

      {/* Timezone Selector */}
      <div className="mb-3 relative" data-testid="timezone-selector">
        <button onClick={() => setShowTzPicker(!showTzPicker)}
          className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-xs text-zinc-300 hover:border-zinc-600 w-full">
          <Clock className="w-3.5 h-3.5 text-purple-400" />
          <span className="flex-1 text-left">{userTimezone}</span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showTzPicker ? 'rotate-180' : ''}`} />
        </button>
        {showTzPicker && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-xl max-h-48 overflow-y-auto z-20 shadow-xl">
            {ALL_TIMEZONES.map(tz => (
              <button key={tz} onClick={() => saveTimezone(tz)}
                className={`w-full px-3 py-2 text-xs text-left hover:bg-zinc-800 transition-colors ${tz === userTimezone ? 'text-purple-400 font-semibold bg-zinc-800' : 'text-zinc-300'}`}
                data-testid={`tz-${tz}`}>{tz}</button>
            ))}
          </div>
        )}
      </div>

      {/* Weekly Grid with AM/PM */}
      <div className="overflow-x-auto no-scrollbar">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-8 gap-0.5 mb-0.5">
            <div className="text-[10px] text-zinc-600 px-1" />
            {DAYS.map((d) => (
              <div key={d} className="text-center text-[10px] font-semibold text-zinc-400 py-1 bg-zinc-900 rounded-t-lg">{d}</div>
            ))}
          </div>
          {HOURS_24.map(hour => (
            <div key={hour} className="grid grid-cols-8 gap-0.5 mb-0.5">
              <div className="text-[10px] text-zinc-500 pr-1 text-right pt-0.5 leading-none whitespace-nowrap font-mono">{formatTo12h(hour)}</div>
              {DAYS.map((_, dayIdx) => {
                const active = isSlotActive(dayIdx, hour);
                const hasBooking = bookings.some(b => {
                  const d = new Date(b.slot_date);
                  return d.getDay() === (dayIdx + 1) % 7 && b.slot_time === hour;
                });
                return (
                  <button key={dayIdx}
                    onClick={() => editing && toggleSlot(dayIdx, hour)}
                    className={`h-7 rounded transition-all ${hasBooking ? 'bg-purple-500/40 border border-purple-500/60' : active ? 'bg-emerald-500/25 border border-emerald-500/40' : 'bg-zinc-900 border border-zinc-800/50'} ${editing ? 'cursor-pointer hover:bg-zinc-700' : 'cursor-default'}`}
                    data-testid={`slot-${dayIdx}-${hour}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 text-[10px] text-zinc-500">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500/25 border border-emerald-500/40" />Available</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-purple-500/40 border border-purple-500/60" />Booked</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-zinc-900 border border-zinc-800/50" />Unavailable</div>
      </div>

      {/* Upcoming Bookings with AM/PM */}
      {bookings.length > 0 && (
        <div className="mt-5">
          <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Upcoming Sessions</h4>
          <div className="space-y-2">
            {bookings.slice(0, 5).map(b => {
              const timeDisplay = b.slot_time ? formatTo12h(b.slot_time) : b.slot_time;
              return (
                <div key={b.booking_id} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5" data-testid={`booking-${b.booking_id}`}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${b.call_type === 'video' ? 'bg-blue-500/15' : 'bg-green-500/15'}`}>
                      {b.call_type === 'video' ? <Video className="w-4 h-4 text-blue-400" /> : <Phone className="w-4 h-4 text-green-400" />}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">{b.client_name || b.expert_name}</p>
                      <p className="text-[10px] text-zinc-500">{b.slot_date} at {timeDisplay} · {b.duration_minutes}min</p>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.status === 'upcoming' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>{b.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──── Referral Dashboard ──── */
function ReferralDashboard({ user, headers, showToast, onProfileClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${API_URL}/api/referral/dashboard`, { headers });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchDashboard();
  }, []);

  if (loading) return <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 animate-pulse"><div className="h-4 bg-zinc-800 rounded w-1/3 mb-3" /><div className="h-8 bg-zinc-800 rounded w-1/2" /></div>;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid="referral-dashboard">
      {/* Header with code */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500/15 flex items-center justify-center">
              <Gift className="w-4 h-4 text-purple-400" />
            </div>
            <span className="text-sm font-bold text-white">Referral Program</span>
          </div>
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-zinc-400 hover:text-white" data-testid="toggle-referral-detail">
            {expanded ? 'Collapse' : 'Details'}
          </button>
        </div>
        <div className="flex items-center justify-between bg-zinc-800 rounded-xl px-3 py-2.5">
          <span className="text-sm font-mono font-bold text-white">{user?.referral_code}</span>
          <button onClick={() => { navigator.clipboard.writeText(user?.referral_code); if (showToast) showToast('Code copied!'); }}
            className="text-xs text-purple-400 hover:text-purple-300 font-medium flex items-center gap-1" data-testid="copy-referral-btn">
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
        <p className="text-[10px] text-zinc-600 mt-1.5">{data?.referral_percentage || 3}% commission for {data?.referral_duration_months || 12} months</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 divide-x divide-zinc-800 border-b border-zinc-800">
        <div className="p-3 text-center">
          <p className="text-2xl font-black text-white">{data?.total_referrals || 0}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">People Referred</p>
        </div>
        <div className="p-3 text-center">
          <p className="text-2xl font-black text-purple-400">₹{(data?.total_earnings || 0).toFixed(2)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Total Income</p>
        </div>
      </div>

      {/* Referred Users List */}
      {expanded && (
        <div className="max-h-64 overflow-y-auto" data-testid="referral-users-list">
          {(!data?.referred_users || data.referred_users.length === 0) ? (
            <div className="p-6 text-center">
              <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-xs text-zinc-500">No referrals yet</p>
              <p className="text-[10px] text-zinc-600 mt-1">Share your code to start earning</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {data.referred_users.map((u, i) => (
                <div key={u.user_id || i} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  onClick={() => onProfileClick?.(u.user_id)} data-testid={`referral-user-${i}`}>
                  <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-xs font-bold shrink-0">
                    {u.picture ? <img src={u.picture.startsWith('http') ? u.picture : `${API_URL}${u.picture}`} alt="" className="w-full h-full object-cover" /> : u.name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{u.name}</p>
                    <p className="text-[10px] text-zinc-500">{u.headline || (u.joined_at ? `Joined ${new Date(u.joined_at).toLocaleDateString()}` : '')}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-bold ${u.total_income > 0 ? 'text-purple-400' : 'text-zinc-600'}`}>
                      {u.total_income > 0 ? `₹${u.total_income.toFixed(2)}` : '₹0'}
                    </p>
                    <p className="text-[9px] text-zinc-600">earned</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ──── Group Events View ──── */
function GroupEventsView({ user, headers, showToast, onProfileClick, isOwn }) {
  const [events, setEvents] = useState([]);
  const [myHosted, setMyHosted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '10:00', duration_minutes: 60, max_participants: 20, price: 0, call_type: 'video' });
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(null);
  const [viewTab, setViewTab] = useState('browse');

  useEffect(() => { fetchEvents(); if (isOwn) fetchMyHosted(); }, []);

  const fetchEvents = async () => {
    try { const res = await fetch(`${API_URL}/api/calls/group-events`); if (res.ok) setEvents(await res.json()); } catch {}
    setLoading(false);
  };
  const fetchMyHosted = async () => {
    try { const res = await fetch(`${API_URL}/api/calls/group-events/my/hosted`, { headers }); if (res.ok) setMyHosted(await res.json()); } catch {}
  };

  const createEvent = async () => {
    if (!form.title || !form.date || !form.time) { showToast('Fill title, date and time'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/calls/group-events`, {
        method: 'POST', headers, body: JSON.stringify(form),
      });
      if (res.ok) { showToast('Event created!'); setShowCreate(false); setForm({ title: '', description: '', date: '', time: '10:00', duration_minutes: 60, max_participants: 20, price: 0, call_type: 'video' }); fetchEvents(); fetchMyHosted(); }
      else { const e = await res.json(); showToast(e.detail || 'Failed'); }
    } catch {}
    setCreating(false);
  };

  const joinEvent = async (eventId) => {
    setJoining(eventId);
    try {
      const res = await fetch(`${API_URL}/api/calls/group-events/${eventId}/join`, { method: 'POST', headers });
      if (res.ok) { showToast('Registered! You\'ll be notified when it starts.'); fetchEvents(); }
      else { const e = await res.json(); showToast(e.detail || 'Failed to join'); }
    } catch {}
    setJoining(null);
  };

  const formatTo12h = (t) => { const h = parseInt(t?.split(':')[0] || 0); if (h === 0) return '12:00 AM'; if (h < 12) return `${h}:${t?.split(':')[1] || '00'} AM`; if (h === 12) return `12:${t?.split(':')[1] || '00'} PM`; return `${h - 12}:${t?.split(':')[1] || '00'} PM`; };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="px-4 py-4" data-testid="group-events-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Create & Manage Group Sessions</h3>
        {isOwn && (
          <button onClick={() => setShowCreate(!showCreate)}
            className="text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-medium rounded-lg transition-colors"
            data-testid="create-event-btn">
            <Plus className="w-3 h-3 inline mr-1" />Create Event
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-4 mb-4 space-y-3" data-testid="create-event-form">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Session Title"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-purple-500/50 placeholder-zinc-600" data-testid="event-title-input" />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" rows={2}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-purple-500/50 placeholder-zinc-600 resize-none" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="event-date-input" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Time</label>
              <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="event-time-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Duration (min)</label>
              <input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Max People</label>
              <input type="number" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: parseInt(e.target.value) || 20 })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 mb-1 block">Price (₹)</label>
              <input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="event-price-input" />
            </div>
          </div>
          <div className="flex gap-2">
            {['video', 'voice'].map(t => (
              <button key={t} onClick={() => setForm({ ...form, call_type: t })}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${form.call_type === t ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                {t === 'video' ? 'Video' : 'Voice'}
              </button>
            ))}
          </div>
          <button onClick={createEvent} disabled={creating}
            className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl text-sm transition-colors disabled:opacity-50"
            data-testid="submit-event-btn">{creating ? 'Creating...' : 'Create Group Session'}</button>
        </div>
      )}

      {/* Browse / My Events toggle */}
      {isOwn && (
        <div className="flex gap-2 mb-3">
          {['browse', 'hosted'].map(t => (
            <button key={t} onClick={() => setViewTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${viewTab === t ? 'bg-zinc-800 text-white' : 'bg-zinc-900 text-zinc-500'}`}
              data-testid={`events-tab-${t}`}>
              {t === 'browse' ? 'All Events' : 'My Events'}
            </button>
          ))}
        </div>
      )}

      {/* Events List */}
      {(viewTab === 'browse' ? events : myHosted).length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-500">{viewTab === 'browse' ? 'No upcoming events' : 'You haven\'t created any events'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(viewTab === 'browse' ? events : myHosted).map(evt => {
            const isHost = evt.host_id === user?.user_id;
            const attendeeList = evt.attendees || evt.participants || [];
            const isJoined = attendeeList.some(a => (typeof a === 'string' ? a : a.user_id) === user?.user_id);
            const spotsTaken = attendeeList.length;
            const spotsLeft = evt.max_participants - spotsTaken;
            return (
              <div key={evt.event_id} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden" data-testid={`event-${evt.event_id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-bold text-white">{evt.title}</p>
                      {evt.description && <p className="text-[11px] text-zinc-400 mt-0.5 line-clamp-2">{evt.description}</p>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0 ml-2 ${evt.price > 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700 text-zinc-300'}`}>
                      {evt.price > 0 ? `₹${evt.price}` : 'Free'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-zinc-500 mb-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{evt.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTo12h(evt.time)}</span>
                    <span className="flex items-center gap-1">{evt.call_type === 'video' ? <Video className="w-3 h-3" /> : <Phone className="w-3 h-3" />}{evt.duration_minutes}min</span>
                  </div>
                  {/* Host info */}
                  {!isHost && (
                    <div className="flex items-center gap-2 mb-3 cursor-pointer" onClick={() => onProfileClick?.(evt.host_id)}>
                      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-[9px] font-bold">
                        {evt.host_picture ? <img src={`${API_URL}${evt.host_picture}`} alt="" className="w-full h-full object-cover" /> : evt.host_name?.[0]}
                      </div>
                      <span className="text-[11px] text-zinc-300 font-medium">Hosted by {evt.host_name}</span>
                    </div>
                  )}
                  {/* Seats & Join */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-zinc-500" />
                      <span className="text-[11px] text-zinc-400">{spotsTaken}/{evt.max_participants} joined</span>
                      {spotsLeft <= 3 && spotsLeft > 0 && <span className="text-[10px] text-orange-400 font-semibold">{spotsLeft} left!</span>}
                    </div>
                    {!isHost && !isJoined && spotsLeft > 0 && (
                      <button onClick={() => joinEvent(evt.event_id)} disabled={joining === evt.event_id}
                        className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-50"
                        data-testid={`join-event-${evt.event_id}`}>
                        {joining === evt.event_id ? '...' : (evt.price > 0 ? `Join ₹${evt.price}` : 'Join Free')}
                      </button>
                    )}
                    {isJoined && <span className="text-[10px] text-emerald-400 font-semibold bg-emerald-500/10 px-2 py-1 rounded-full">Registered</span>}
                    {isHost && <span className="text-[10px] text-purple-400 font-semibold bg-purple-500/10 px-2 py-1 rounded-full">Your Event</span>}
                    {spotsLeft <= 0 && !isHost && !isJoined && <span className="text-[10px] text-red-400 font-semibold">Full</span>}
                  </div>
                </div>
                {/* Attendee avatars */}
                {attendeeList.length > 0 && (
                  <div className="px-4 py-2 border-t border-zinc-800 flex items-center gap-1.5">
                    <span className="text-[9px] text-zinc-600 mr-1">Attending:</span>
                    {attendeeList.slice(0, 6).map((a, i) => {
                      const aId = typeof a === 'string' ? a : a.user_id;
                      const aName = typeof a === 'string' ? '?' : (a.name?.[0] || '?');
                      const aPic = typeof a === 'string' ? null : a.picture;
                      return (
                        <div key={i} className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-[7px] font-bold -ml-1 first:ml-0 border border-zinc-900 cursor-pointer"
                          onClick={() => onProfileClick?.(aId)}>
                          {aPic ? <img src={aPic.startsWith('http') ? aPic : `${API_URL}${aPic}`} alt="" className="w-full h-full object-cover" /> : aName}
                        </div>
                      );
                    })}
                    {attendeeList.length > 6 && <span className="text-[9px] text-zinc-500">+{attendeeList.length - 6}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ──── Files View ──── */

/* ──── Saved / Bookmarked Posts ──── */
function SavedPostsView({ headers, onProfileClick, onClose }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewPost, setViewPost] = useState(null);
  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const res = await fetch(`${API_URL}/api/posts/saved`, { headers });
        if (res.ok) setPosts(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchSaved();
  }, []);
  const goToProfile = (uid) => {
    setViewPost(null);
    if (onClose) onClose();
    if (onProfileClick) onProfileClick(uid);
  };
  if (loading) return <div className="p-4 text-center text-zinc-500 text-xs">Loading saved posts...</div>;
  if (posts.length === 0) return <div className="p-8 text-center"><BookmarkIcon className="w-8 h-8 text-zinc-700 mx-auto mb-2" /><p className="text-sm text-zinc-500">No saved posts yet</p><p className="text-xs text-zinc-600 mt-1">Bookmark posts from the feed to find them here</p></div>;
  return (
    <div className="space-y-3 p-3" data-testid="saved-posts-list">
      {posts.map(p => (
        <div key={p.post_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 cursor-pointer hover:border-zinc-600 transition-colors"
          onClick={() => setViewPost(p)} data-testid={`saved-${p.post_id}`}>
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden text-[9px] font-bold cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goToProfile(p.user_id); }}
              data-testid={`saved-profile-${p.user_id}`}>
              {p.user_picture ? <img src={p.user_picture} alt="" className="w-full h-full object-cover" /> : p.user_name?.[0]}
            </div>
            <span className="text-xs font-semibold text-white cursor-pointer hover:text-purple-400"
              onClick={(e) => { e.stopPropagation(); goToProfile(p.user_id); }}>{p.user_name}</span>
            <span className="text-[10px] text-zinc-600">{new Date(p.created_at).toLocaleDateString()}</span>
          </div>
          {p.media_path && (
            <div className="rounded-xl overflow-hidden mb-2 aspect-video bg-zinc-800">
              <img src={p.media_path.startsWith('http') ? p.media_path : `${API_URL}/api/storage/${p.media_path}`} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <p className="text-sm text-zinc-300">{p.content?.substring(0, 200)}{p.content?.length > 200 ? '...' : ''}</p>
        </div>
      ))}
      {/* Full screen view */}
      {viewPost && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col" onClick={() => setViewPost(null)} data-testid="saved-post-fullscreen">
          <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => goToProfile(viewPost.user_id)} data-testid="saved-fs-profile-click">
              <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden">
                {viewPost.user_picture ? <img src={viewPost.user_picture} alt="" className="w-full h-full object-cover" /> :
                  <span className="w-full h-full flex items-center justify-center text-xs font-bold">{viewPost.user_name?.[0]}</span>}
              </div>
              <span className="text-sm font-bold text-white hover:text-purple-400 transition-colors">{viewPost.user_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => goToProfile(viewPost.user_id)}
                className="text-[11px] bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-full font-semibold"
                data-testid="saved-view-profile-btn">View Profile</button>
              <button onClick={() => setViewPost(null)}><X className="w-6 h-6 text-white" /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4" onClick={e => e.stopPropagation()}>
            {viewPost.media_path ? (
              <img src={viewPost.media_path.startsWith('http') ? viewPost.media_path : `${API_URL}/api/storage/${viewPost.media_path}`} alt="" className="max-w-full max-h-full object-contain rounded-xl" />
            ) : (
              <div className="w-full max-w-lg rounded-2xl p-8" style={{ backgroundColor: viewPost.bg_color || '#18181b' }}>
                <p className="text-white text-2xl font-bold text-center leading-relaxed">{viewPost.content}</p>
              </div>
            )}
          </div>
          {viewPost.content && viewPost.media_path && (
            <div className="px-4 py-3 bg-zinc-900/80" onClick={e => e.stopPropagation()}>
              <p className="text-sm text-white"><span className="font-bold mr-1 cursor-pointer hover:text-purple-400" onClick={() => goToProfile(viewPost.user_id)}>{viewPost.user_name}</span>{viewPost.content}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilesView({ headers, showToast }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [shareFile, setShareFile] = useState(null);
  const [connections, setConnections] = useState([]);
  const [shareBusy, setShareBusy] = useState(false);
  const [connSearch, setConnSearch] = useState('');

  useEffect(() => { fetchFiles(); }, []);

  const fetchFiles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/files/shared`, { headers });
      if (res.ok) setFiles(await res.json());
    } catch {}
    setLoading(false);
  };

  const openSharePicker = async (f) => {
    setShareFile(f);
    try {
      const res = await fetch(`${API_URL}/api/connections/followers`, { headers });
      if (res.ok) setConnections(await res.json());
    } catch {}
  };

  const sendFileToUser = async (userId) => {
    if (!shareFile) return;
    setShareBusy(true);
    try {
      // Download the file, then re-send via /api/dm/send-file (free)
      const dl = await fetch(`${API_URL}/api/files/download/${shareFile.file_id}`, { headers });
      if (!dl.ok) { showToast?.('Cannot fetch file'); setShareBusy(false); return; }
      const blob = await dl.blob();
      const fd = new FormData();
      fd.append('file', new File([blob], shareFile.original_name || shareFile.filename || 'file'));
      const r = await fetch(`${API_URL}/api/dm/send-file?recipient_id=${userId}`, {
        method: 'POST', headers: { 'Authorization': headers.Authorization }, body: fd,
      });
      if (r.ok) { showToast?.('File sent (free)'); setShareFile(null); }
      else showToast?.('Failed to send');
    } catch { showToast?.('Network error'); }
    setShareBusy(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/files/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
      });
      if (res.ok) { showToast('File uploaded!'); fetchFiles(); }
      else { showToast('Upload failed'); }
    } catch { showToast('Upload error'); }
    setUploading(false);
  };

  const deleteFile = async (fileId) => {
    try {
      const res = await fetch(`${API_URL}/api/files/${fileId}`, { method: 'DELETE', headers });
      if (res.ok) { showToast('File deleted'); fetchFiles(); }
    } catch {}
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="px-4 py-4" data-testid="files-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white">My Files</h3>
        <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-white font-medium cursor-pointer transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-upload-input" />
        </label>
      </div>

      {files.length === 0 ? (
        <div className="text-center py-12 text-zinc-600">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No files yet</p>
          <p className="text-xs text-zinc-700 mt-1">Upload documents, images, or any files</p>
        </div>
      ) : (
        <div className="space-y-2">
          {files.map(f => (
            <div key={f.file_id} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5" data-testid={`file-${f.file_id}`}>
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4 text-zinc-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-white truncate">{f.original_name || f.filename}</p>
                  <p className="text-[10px] text-zinc-500">{formatSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => openSharePicker(f)} className="text-[10px] text-emerald-400 hover:text-emerald-300 font-medium px-2 py-1 bg-zinc-800 rounded-lg flex items-center gap-1" data-testid={`share-file-${f.file_id}`}>
                  <Send className="w-3 h-3" /> Share
                </button>
                <a href={`${API_URL}/api/files/download/${f.file_id}`} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blue-400 hover:text-blue-300 font-medium px-2 py-1 bg-zinc-800 rounded-lg" data-testid={`download-${f.file_id}`}>
                  Download
                </a>
                <button onClick={() => deleteFile(f.file_id)} className="p-1 text-zinc-600 hover:text-red-400" data-testid={`delete-file-${f.file_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Share Picker Modal */}
      {shareFile && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setShareFile(null)} data-testid="share-file-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white text-sm">Share File (Free DM)</h3>
                <p className="text-[10px] text-zinc-500 mt-0.5 truncate max-w-[260px]">{shareFile.original_name || shareFile.filename}</p>
              </div>
              <button onClick={() => setShareFile(null)} data-testid="close-share-modal"><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="p-3 border-b border-zinc-800">
              <input value={connSearch} onChange={e => setConnSearch(e.target.value)} placeholder="Search connections..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-full px-4 py-2 text-xs outline-none" />
            </div>
            <div className="overflow-y-auto max-h-[50vh]">
              {connections.filter(c => !connSearch || c.name?.toLowerCase().includes(connSearch.toLowerCase()) || c.username?.toLowerCase().includes(connSearch.toLowerCase())).map(c => (
                <button key={c.user_id} onClick={() => sendFileToUser(c.user_id)} disabled={shareBusy}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 disabled:opacity-50 transition-colors" data-testid={`share-to-${c.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden shrink-0">
                    {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="w-full h-full flex items-center justify-center text-xs font-bold text-white">{c.name?.[0]}</span>}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{c.name}</p>
                    <p className="text-[10px] text-zinc-500">@{c.username}</p>
                  </div>
                  <Send className="w-4 h-4 text-emerald-400" />
                </button>
              ))}
              {connections.length === 0 && <p className="text-xs text-zinc-500 text-center py-8">No connections yet</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ──── Favorites View ──── */
function FavoritesView({ headers, onProfileClick }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchFavorites(); }, []);

  const fetchFavorites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/favorites`, { headers });
      if (res.ok) setFavorites(await res.json());
    } catch {}
    setLoading(false);
  };

  const removeFavorite = async (userId) => {
    try {
      await fetch(`${API_URL}/api/favorites/${userId}`, { method: 'POST', headers });
      setFavorites(prev => prev.filter(f => f.user_id !== userId));
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="px-4 py-4" data-testid="favorites-view">
      <h3 className="text-sm font-bold text-white mb-3">Saved Experts</h3>
      {favorites.length === 0 ? (
        <div className="text-center py-12 text-zinc-600">
          <Heart className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No saved experts yet</p>
          <p className="text-xs text-zinc-700 mt-1">Tap the heart on any expert's profile to save them</p>
        </div>
      ) : (
        <div className="space-y-2">
          {favorites.map(fav => (
            <div key={fav.user_id} className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5" data-testid={`fav-${fav.user_id}`}>
              <button onClick={() => onProfileClick?.(fav.user_id)} className="flex items-center gap-2.5 min-w-0 flex-1 text-left">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 p-[1.5px] shrink-0">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                    {fav.picture ? <img src={`${API_URL}${fav.picture}`} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-xs font-bold">{fav.name?.[0]}</span>}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{fav.name}</p>
                  <p className="text-[10px] text-zinc-500">@{fav.username || 'user'}</p>
                </div>
              </button>
              <button onClick={() => removeFavorite(fav.user_id)} className="p-2 text-red-400 hover:text-red-300" data-testid={`unsave-${fav.user_id}`}>
                <Heart className="w-4 h-4 fill-current" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──── Session History View ──── */
function SessionHistoryView({ headers, user }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptId, setReceiptId] = useState(null);

  useEffect(() => { fetchHistory(); }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/session-history`, { headers });
      if (res.ok) setSessions(await res.json());
    } catch {}
    setLoading(false);
  };

  const activeReceipt = sessions.find(s => s.booking_id === receiptId);

  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="px-4 py-4" data-testid="session-history">
      <h3 className="text-sm font-bold text-white mb-3">Session History</h3>

      {sessions.length === 0 ? (
        <div className="text-center py-12 text-zinc-600">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No sessions yet</p>
          <p className="text-xs text-zinc-700 mt-1">Your booking history will appear here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.booking_id} data-testid={`session-${s.booking_id}`}>
              <button onClick={() => setReceiptId(receiptId === s.booking_id ? null : s.booking_id)}
                className="w-full flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-3 text-left hover:bg-zinc-800/80 transition-colors">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.call_type === 'video' ? 'bg-blue-500/15' : s.call_type === 'voice' ? 'bg-green-500/15' : 'bg-purple-500/15'}`}>
                    {s.call_type === 'video' ? <Video className="w-4 h-4 text-blue-400" /> :
                      s.call_type === 'voice' ? <Phone className="w-4 h-4 text-green-400" /> :
                      <MessageSquare className="w-4 h-4 text-purple-400" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-white truncate">
                      {s.role === 'client' ? s.expert_name : s.client_name}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      {s.call_type} · {s.slot_date || new Date(s.created_at).toLocaleDateString()}
                      {s.slot_time ? ` at ${s.slot_time}` : ''}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xs font-bold ${s.role === 'client' ? 'text-orange-400' : 'text-emerald-400'}`}>
                    {s.role === 'client' ? '-' : '+'}₹{(s.payment_details?.amount || s.price || 0).toFixed(2)}
                  </p>
                  <p className={`text-[10px] ${s.status === 'completed' ? 'text-emerald-400' : s.status === 'upcoming' ? 'text-blue-400' : s.status === 'cancelled' ? 'text-red-400' : 'text-zinc-500'}`}>{s.status}</p>
                </div>
              </button>

              {/* Receipt Expansion */}
              {receiptId === s.booking_id && (
                <div className="bg-zinc-950 border border-zinc-800 border-t-0 rounded-b-xl px-4 py-3 -mt-1 space-y-2" data-testid={`receipt-${s.booking_id}`}>
                  <div className="text-center border-b border-zinc-800 pb-2 mb-2">
                    <p className="text-xs font-bold text-white">Receipt</p>
                    <p className="text-[10px] text-zinc-600">{s.booking_id}</p>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-zinc-400">Type</span>
                    <span className="text-white font-medium">{s.call_type?.charAt(0).toUpperCase() + s.call_type?.slice(1)} {s.duration_minutes ? `(${s.duration_minutes}min)` : ''}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-zinc-400">{s.role === 'client' ? 'Expert' : 'Client'}</span>
                    <span className="text-white">{s.role === 'client' ? s.expert_name : s.client_name}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-zinc-400">Date</span>
                    <span className="text-white">{s.slot_date || new Date(s.created_at).toLocaleDateString()} {s.slot_time || ''}</span>
                  </div>
                  {s.payment_details && (
                    <>
                      <div className="border-t border-zinc-800 pt-2 mt-2" />
                      <div className="flex justify-between text-[11px]">
                        <span className="text-zinc-400">Amount</span>
                        <span className="text-white font-bold">₹{(s.payment_details.amount || 0).toFixed(2)}</span>
                      </div>
                      {s.role === 'expert' && s.payment_details.commission > 0 && (
                        <div className="flex justify-between text-[11px]">
                          <span className="text-zinc-400">Platform Fee</span>
                          <span className="text-zinc-500">-₹{s.payment_details.commission.toFixed(2)}</span>
                        </div>
                      )}
                      {s.role === 'expert' && (
                        <div className="flex justify-between text-[11px] font-bold">
                          <span className="text-zinc-400">You Received</span>
                          <span className="text-emerald-400">₹{((s.payment_details.amount || 0) - (s.payment_details.commission || 0)).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-[11px]">
                        <span className="text-zinc-400">Payment</span>
                        <span className={`${s.payment_details.status === 'paid' ? 'text-emerald-400' : 'text-yellow-400'}`}>{s.payment_details.method} · {s.payment_details.status}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function PriceTag({ label, price, icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="flex items-center space-x-2 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 hover:border-zinc-600 hover:bg-zinc-800 transition-all cursor-pointer active:scale-95" data-testid={`price-tag-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="w-4 h-4 text-zinc-500" /><span className="text-xs text-zinc-400 font-medium">{label}</span><span className="text-sm font-black text-white">₹{price}</span>
    </button>
  );
}

function ConnectButton({ userId, large }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  useEffect(() => {
    fetch(`${API_URL}/api/connections/check/${userId}`, { headers }).then(r => r.json()).then(d => { setConnected(d.is_connected); setLoading(false); }).catch(() => setLoading(false));
  }, [userId]);
  const toggle = async () => {
    const res = await fetch(`${API_URL}/api/connect/${userId}`, { method: connected ? 'DELETE' : 'POST', headers });
    if (res.ok) setConnected(!connected);
  };
  if (loading) return <div className="flex-1 bg-zinc-800 rounded-xl py-3 animate-pulse" />;
  return (
    <button onClick={toggle}
      className={`flex-1 rounded-xl font-bold transition-colors flex items-center justify-center gap-1.5 ${large ? 'py-3 text-sm' : 'py-2 text-xs'} ${connected ? 'bg-zinc-800 border border-zinc-700 text-white' : 'bg-white text-black'}`}
      data-testid="connect-btn">
      <Users className="w-4 h-4" />
      {connected ? 'Connected' : 'Connect'}
    </button>
  );
}

function JoinCommunityButton({ userId, showToast }) {
  const [joined, setJoined] = useState(false);
  const [communityId, setCommunityId] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/communities/${userId}`, { headers });
        if (res.ok) {
          const c = await res.json();
          setCommunityId(c.community_id);
          setJoined(c.members?.includes && c.is_member || false);
          // Check if already joined
          const jRes = await fetch(`${API_URL}/api/communities/joined/list`, { headers });
          if (jRes.ok) {
            const joined_list = await jRes.json();
            setJoined(joined_list.some(j => j.community_id === c.community_id));
          }
        }
      } catch {}
      setLoading(false);
    })();
  }, [userId]);
  const toggle = async () => {
    if (!communityId) return;
    try {
      if (joined) {
        await fetch(`${API_URL}/api/communities/${communityId}/leave`, { method: 'POST', headers });
        setJoined(false);
        showToast?.('Left community');
      } else {
        await fetch(`${API_URL}/api/communities/${communityId}/join`, { method: 'POST', headers });
        setJoined(true);
        showToast?.('Joined community!');
      }
    } catch {}
  };
  if (loading) return <div className="bg-zinc-800 rounded-xl py-2.5 px-4 animate-pulse w-24" />;
  if (!communityId) return null;
  return (
    <button onClick={toggle}
      className={`px-4 rounded-xl py-2.5 text-[11px] font-bold transition-colors ${joined ? 'bg-zinc-800 border border-zinc-700 text-zinc-300' : 'bg-zinc-800 border border-zinc-700 text-white hover:bg-zinc-700'}`}
      data-testid="join-community-btn">
      {joined ? 'Joined' : 'Join Community'}
    </button>
  );
}

function FavoriteButton({ userId }) {
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  useEffect(() => {
    fetch(`${API_URL}/api/favorites/check/${userId}`, { headers }).then(r => r.json()).then(d => { setSaved(d.is_favorite); setLoading(false); }).catch(() => setLoading(false));
  }, [userId]);
  const toggle = async () => {
    const res = await fetch(`${API_URL}/api/favorites/${userId}`, { method: 'POST', headers });
    if (res.ok) { const d = await res.json(); setSaved(d.saved); }
  };
  if (loading) return <div className="bg-zinc-800 rounded-xl p-2.5 animate-pulse w-10 h-10" />;
  return (
    <button onClick={toggle} className={`bg-zinc-800 border border-zinc-700 rounded-xl p-2.5 hover:bg-zinc-700 transition-colors ${saved ? 'text-red-400' : 'text-zinc-400'}`} data-testid="favorite-btn">
      <Heart className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
    </button>
  );
}


function SettingsModal({ user, onClose, onSaved, calendarLinked, onLinkCalendar, onUnlinkCalendar, headers, showToast, onProfileClick }) {
  const [form, setForm] = useState({
    name: user.name || '', headline: user.headline || '', bio: user.bio || '',
    city: user.city || '', country: user.country || '',
    company: user.company || '', position: user.position || '',
    education: user.education || '', website: user.website || '',
  });
  const [saving, setSaving] = useState(false);
  const [certUploading, setCertUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const token = localStorage.getItem('token');
  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API_URL}/api/profile`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) onSaved();
    setSaving(false);
  };
  const uploadCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetch(`${API_URL}/api/profile/certificates`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData,
      });
    } catch {}
    setCertUploading(false);
  };
  const fields = [
    { key: 'name', label: 'Name', type: 'input' },
    { key: 'headline', label: 'Headline', type: 'input', typeahead: 'role' },
    { key: 'position', label: 'Current Position / Role', type: 'input', typeahead: 'position' },
    { key: 'company', label: 'Company / Organisation', type: 'input', typeahead: 'company' },
    { key: 'education', label: 'Education', type: 'input', typeahead: 'college' },
    { key: 'bio', label: 'Bio', type: 'textarea' },
    { key: 'city', label: 'City', type: 'input', typeahead: 'city' },
    { key: 'country', label: 'Country', type: 'input', typeahead: 'location' },
    { key: 'website', label: 'Website', type: 'input' },
  ];

  const sections = [
    { id: 'profile', label: 'Edit Profile', icon: Settings },
    { id: 'history', label: 'Session History', icon: Clock },
    { id: 'saved_posts', label: 'Saved Posts', icon: BookmarkIcon },
    { id: 'favorites', label: 'Saved Profiles', icon: Heart },
    { id: 'earnings', label: 'Total Earnings', icon: DollarSign },
    { id: 'files', label: 'My Files', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Account Privacy', icon: Shield },
    { id: 'blocked', label: 'Blocked Users', icon: Shield },
    { id: 'account', label: 'Account', icon: Settings },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl border border-zinc-800 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="settings-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 shrink-0">
          <h3 className="font-bold text-lg">Settings</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 px-4 py-2 overflow-x-auto no-scrollbar border-b border-zinc-800 shrink-0">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`settings-section-${s.id}`}>
              <s.icon className="w-3 h-3" />{s.label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {activeSection === 'profile' && (
            <>
              <div className="space-y-3">
                {fields.map(f => (
                  <div key={f.key}><label className="block text-xs text-zinc-400 mb-1">{f.label}</label>
                    {f.type === 'textarea' ? <textarea value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none" rows={3} data-testid={`settings-${f.key}`} />
                      : f.typeahead ? <TypeaheadInput value={form[f.key]} onChange={v => setForm({ ...form, [f.key]: v })} field={f.typeahead} placeholder={f.label} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white placeholder-zinc-500" data-testid={`settings-${f.key}`} />
                      : <input type="text" value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none" data-testid={`settings-${f.key}`} />}
                  </div>))}
              </div>
              <button onClick={save} disabled={saving} className="w-full bg-white text-black font-bold rounded-full py-3 text-sm mt-4 disabled:opacity-50" data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save'}</button>
            </>
          )}

          {activeSection === 'history' && <SessionHistoryView headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} user={user} />}
          {activeSection === 'saved_posts' && <SavedPostsView headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} onProfileClick={onProfileClick} onClose={onClose} />}
          {activeSection === 'favorites' && <FavoritesView headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} onProfileClick={(uid) => { onClose(); onProfileClick?.(uid); }} />}
          {activeSection === 'earnings' && <EarningsView headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} />}
          {activeSection === 'files' && <FilesView headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} showToast={showToast} />}
          {activeSection === 'analytics' && <AnalyticsDashboard headers={headers || { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }} />}
          {activeSection === 'account' && (
            <div className="space-y-4">
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-xs text-zinc-400 mb-1">Email</p>
                <p className="text-sm text-white font-medium">{user?.email}</p>
              </div>
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-xs text-zinc-400 mb-1">Username</p>
                <p className="text-sm text-white font-medium">@{user?.username || 'not set'}</p>
              </div>
              <div className="bg-zinc-800 rounded-xl p-4">
                <p className="text-xs text-zinc-400 mb-1">Member Since</p>
                <p className="text-sm text-white font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
              </div>
              {/* Birthday */}
              <div className="bg-zinc-800 rounded-xl p-4">
                <label className="text-xs text-zinc-400 mb-1 block">Birthday <span className="text-zinc-600">(used for birthday banners on Connect)</span></label>
                <input type="date" defaultValue={user?.birthday || ''}
                  onBlur={async (e) => {
                    const v = e.target.value;
                    if (v && v !== user?.birthday) {
                      try {
                        await fetch(`${API_URL}/api/auth/birthday?birthday=${encodeURIComponent(v)}`,
                          { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } });
                        showToast?.('Birthday saved!');
                      } catch {}
                    }
                  }}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-sm outline-none text-white"
                  data-testid="settings-birthday" />
              </div>
              <button className="w-full py-3 bg-red-500/10 text-red-400 font-bold rounded-xl text-sm hover:bg-red-500/20 transition-colors" data-testid="logout-settings-btn"
                onClick={() => { localStorage.clear(); window.location.href = '/'; }}>
                Log Out
              </button>
              <button className="w-full py-3 bg-red-600/10 border border-red-600/30 text-red-500 font-bold rounded-xl text-sm hover:bg-red-600/20 transition-colors" data-testid="delete-account-btn"
                onClick={async () => {
                  if (window.confirm('This action is permanent. Are you sure you want to delete your account?')) {
                    try {
                      const res = await fetch(`${API_URL}/api/auth/account`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
                      if (res.ok) { localStorage.clear(); window.location.href = '/'; }
                    } catch {}
                  }
                }}>
                <Trash2 className="w-4 h-4 inline mr-1" /> Delete Account
              </button>
            </div>
          )}

          {activeSection === 'notifications' && <NotificationSettingsView token={token} user={user} />}
          {activeSection === 'privacy' && <PrivacySettingsView token={token} />}
          {activeSection === 'blocked' && <BlockedUsersView token={token} onProfileClick={(uid) => { onClose(); onProfileClick?.(uid); }} />}
        </div>
      </div>
    </div>
  );
}

/* ──── Earnings View (for settings) ──── */

function NotificationSettingsView({ token, user }) {
  const [settings, setSettings] = useState({ push: true, email: true, community: true, connection: true, like: true, comment: true, share: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/notifications/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) setSettings(await res.json());
      } catch {}
    })();
  }, []);
  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/notifications/settings`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(settings) });
    setSaving(false);
  };
  const items = [
    { key: 'push', label: 'Push Notifications', desc: 'Alerts for messages, bookings, etc.' },
    { key: 'email', label: 'Email Notifications', desc: 'Booking confirmations & reminders' },
    { key: 'community', label: 'Community', desc: 'Updates from joined communities' },
    { key: 'connection', label: 'Connections', desc: 'New follow/connect requests' },
    { key: 'like', label: 'Likes', desc: 'When someone likes your post' },
    { key: 'comment', label: 'Comments', desc: 'Comments on your posts' },
    { key: 'share', label: 'Shares', desc: 'When someone shares your content' },
  ];
  return (
    <div className="space-y-3" data-testid="notification-settings">
      {items.map(i => (
        <div key={i.key} className="bg-zinc-800 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-white">{i.label}</p>
            <p className="text-[10px] text-zinc-500">{i.desc}</p>
          </div>
          <button onClick={() => toggle(i.key)}
            className={`relative w-10 h-5 rounded-full transition-colors ${settings[i.key] ? 'bg-emerald-500/40' : 'bg-zinc-600'}`}
            data-testid={`notif-toggle-${i.key}`}>
            <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${settings[i.key] ? 'translate-x-5 bg-emerald-400' : 'translate-x-0.5 bg-zinc-400'}`} />
          </button>
        </div>
      ))}
      <button onClick={save} disabled={saving} className="w-full bg-white text-black font-bold rounded-full py-3 text-sm mt-3 disabled:opacity-50" data-testid="save-notif-settings">
        {saving ? 'Saving...' : 'Save Notification Settings'}
      </button>
    </div>
  );
}

function PrivacySettingsView({ token }) {
  const [settings, setSettings] = useState({ like: true, comment: true, share: true, connection: true, community: true, post_save: true, show_community: true, show_connected: true, show_connecting: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/privacy/settings`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          // Merge so missing keys default to true
          setSettings(prev => ({ ...prev, ...d }));
        }
      } catch {}
    })();
  }, []);
  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  const save = async () => {
    setSaving(true);
    await fetch(`${API_URL}/api/privacy/settings`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(settings) });
    setSaving(false);
  };
  const sections = [
    {
      title: 'Engagement Controls',
      desc: 'Block specific interactions on your posts',
      items: [
        { key: 'like', label: 'Allow Likes', desc: 'Off → no one can like your posts and you wont see likes' },
        { key: 'comment', label: 'Allow Comments', desc: 'Off → comments are blocked on your posts' },
        { key: 'share', label: 'Allow Shares', desc: 'Off → share button hidden on your posts' },
        { key: 'post_save', label: 'Allow Post Save', desc: 'Off → save/bookmark button hidden on your posts' },
      ]
    },
    {
      title: 'Social Controls',
      desc: 'Manage how others interact with your profile',
      items: [
        { key: 'connection', label: 'Allow New Connections', desc: 'Off → users cannot connect with you' },
        { key: 'community', label: 'Allow Community Joins', desc: 'Off → users cannot join your community' },
      ]
    },
    {
      title: 'Profile Visibility',
      desc: 'Hide sections from your public profile',
      items: [
        { key: 'show_community', label: 'Show Community Count', desc: 'Off → hide Community stat on your profile' },
        { key: 'show_connected', label: 'Show Connected Count', desc: 'Off → hide Connected (followers) stat' },
        { key: 'show_connecting', label: 'Show Connecting Count', desc: 'Off → hide Connecting (following) stat' },
      ]
    },
  ];
  return (
    <div className="space-y-4" data-testid="privacy-settings">
      <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 mb-1">
        <p className="text-xs text-purple-300 font-bold mb-0.5">Account Privacy</p>
        <p className="text-[10px] text-zinc-400">Turn any toggle off to disable that interaction or hide that section.</p>
      </div>
      {sections.map((sec, idx) => (
        <div key={idx} className="space-y-2">
          <div className="flex items-baseline justify-between px-1">
            <p className="text-[11px] font-bold text-white uppercase tracking-wider">{sec.title}</p>
          </div>
          <p className="text-[10px] text-zinc-500 px-1 -mt-1.5 mb-1">{sec.desc}</p>
          {sec.items.map(i => (
            <div key={i.key} className="bg-zinc-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex-1 mr-3">
                <p className="text-sm font-medium text-white">{i.label}</p>
                <p className="text-[10px] text-zinc-500">{i.desc}</p>
              </div>
              <button onClick={() => toggle(i.key)}
                className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${settings[i.key] ? 'bg-emerald-500/40' : 'bg-zinc-600'}`}
                data-testid={`privacy-toggle-${i.key}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${settings[i.key] ? 'translate-x-5 bg-emerald-400' : 'translate-x-0.5 bg-zinc-400'}`} />
              </button>
            </div>
          ))}
        </div>
      ))}
      <button onClick={save} disabled={saving} className="w-full bg-white text-black font-bold rounded-full py-3 text-sm mt-3 disabled:opacity-50" data-testid="save-privacy-settings">
        {saving ? 'Saving...' : 'Save Privacy Settings'}
      </button>
    </div>
  );
}

function BlockedUsersView({ token, onProfileClick }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/blocked`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) setUsers(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, []);
  const unblock = async (userId) => {
    await fetch(`${API_URL}/api/block/${userId}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
    setUsers(prev => prev.filter(u => u.user_id !== userId));
  };
  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-2" data-testid="blocked-users">
      {users.length === 0 ? (
        <p className="text-center text-zinc-500 text-sm py-8">No blocked users</p>
      ) : users.map(u => (
        <div key={u.user_id} className="flex items-center gap-3 bg-zinc-800 rounded-xl p-3">
          <div className="w-10 h-10 rounded-full bg-zinc-700 overflow-hidden shrink-0 cursor-pointer" onClick={() => onProfileClick?.(u.user_id)}>
            {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> :
              <span className="w-full h-full flex items-center justify-center text-sm font-bold">{u.name?.[0]}</span>}
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onProfileClick?.(u.user_id)}>
            <p className="text-sm font-semibold text-white truncate">{u.name}</p>
            <p className="text-[10px] text-zinc-500">@{u.username}</p>
          </div>
          <button onClick={() => unblock(u.user_id)} className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded-lg font-bold" data-testid={`unblock-${u.user_id}`}>
            Unblock
          </button>
        </div>
      ))}
    </div>
  );
}


function EarningsView({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/wallet`, { headers });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchEarnings();
  }, []);
  if (loading) return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div data-testid="earnings-view" className="space-y-3">
      <div className="bg-gradient-to-br from-emerald-500/10 to-zinc-900 border border-emerald-500/20 rounded-2xl p-5 text-center">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Earnings</p>
        <p className="text-4xl font-black text-emerald-400">₹{(data?.total_earnings || 0).toFixed(2)}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-800 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-white">₹{(data?.balance || 0).toFixed(2)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Current Balance</p>
        </div>
        <div className="bg-zinc-800 rounded-xl p-4 text-center">
          <p className="text-2xl font-black text-orange-400">₹{(data?.total_spent || 0).toFixed(2)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Total Spent</p>
        </div>
      </div>
      {data?.referral_earnings > 0 && (
        <div className="bg-zinc-800 rounded-xl p-4 text-center">
          <p className="text-xl font-black text-purple-400">₹{data.referral_earnings.toFixed(2)}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Referral Earnings</p>
        </div>
      )}
      {(data?.transactions || []).length > 0 && (
        <div>
          <p className="text-xs text-zinc-400 font-semibold mb-2">Recent Transactions</p>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {data.transactions.map((t, i) => (
              <div key={t.txn_id || i} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2">
                <span className="text-[11px] text-zinc-300 truncate max-w-[200px]">{t.description}</span>
                <span className={`text-xs font-bold ${t.amount > 0 ? 'text-emerald-400' : 'text-orange-400'}`}>{t.amount > 0 ? '+' : ''}₹{Math.abs(t.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MonetizationModal({ user, onClose, onSaved }) {
  const [toggles, setToggles] = useState({ monetization_dm: user.monetization_dm !== false, monetization_voice_note: user.monetization_voice_note !== false, monetization_voice_call: user.monetization_voice_call !== false, monetization_video_call: user.monetization_video_call !== false, monetization_group_session: user.monetization_group_session !== false });
  const [prices, setPrices] = useState({ price_dm: user.price_dm || 5, price_voice_note: user.price_voice_note || 10, price_voice_call: user.price_voice_call || 50, price_video_call: user.price_video_call || 75, price_group_session: user.price_group_session || 99 });
  const [saving, setSaving] = useState(false);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [currency, setCurrency] = useState(user.currency || 'INR');
  const CURRENCIES = ['INR','USD','EUR','GBP','AED','AUD','CAD','SGD','JPY','CNY','KRW','BRL','MXN','ZAR','SAR','THB','PHP','IDR','MYR','NGN','KES','BDT','PKR','LKR','NPR','EGP','TRY'];
  const [sessionForm, setSessionForm] = useState({ title: '', description: '', topic: '', date: '', time_from: '10:00', time_to: '11:00', max_participants: '', price: '', call_type: 'video', cover_image: '' });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const methods = [
    { key: 'dm', label: 'Paid DMs', icon: MessageSquare, color: 'text-blue-400' },
    { key: 'voice_note', label: 'Voice Notes', icon: Phone, color: 'text-green-400' },
    { key: 'voice_call', label: 'Voice Calls', icon: Phone, color: 'text-purple-400' },
    { key: 'video_call', label: 'Video Calls', icon: Video, color: 'text-orange-400' },
    { key: 'group_session', label: 'Group Sessions', icon: Users, color: 'text-pink-400' },
  ];
  const save = async () => {
    setSaving(true);
    await Promise.all([
      fetch(`${API_URL}/api/profile/monetization`, { method: 'PUT', headers, body: JSON.stringify(toggles) }),
      fetch(`${API_URL}/api/profile/pricing`, { method: 'PUT', headers, body: JSON.stringify({ ...prices, currency }) }),
    ]);
    onSaved();
    setSaving(false);
  };

  const handleCoverChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); }
  };

  const createGroupSession = async () => {
    if (!sessionForm.title || !sessionForm.date || !sessionForm.time_from || !sessionForm.time_to) return;
    setCreatingSession(true);
    try {
      let coverPath = '';
      if (coverFile) {
        const fd = new FormData();
        fd.append('file', coverFile);
        const upRes = await fetch(`${API_URL}/api/calls/group-events/upload-cover`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd,
        });
        if (upRes.ok) { const upD = await upRes.json(); coverPath = upD.storage_path; }
      }
      // Calculate duration from time_from and time_to
      const [fh, fm] = sessionForm.time_from.split(':').map(Number);
      const [th, tm] = sessionForm.time_to.split(':').map(Number);
      const durationMin = Math.max(15, (th * 60 + tm) - (fh * 60 + fm));
      const body = {
        title: sessionForm.title,
        description: sessionForm.description,
        topic: sessionForm.topic || sessionForm.title,
        date: sessionForm.date,
        time: sessionForm.time_from,
        time_to: sessionForm.time_to,
        duration_minutes: durationMin,
        max_participants: parseInt(sessionForm.max_participants) || 20,
        price: parseFloat(sessionForm.price) || 0,
        call_type: sessionForm.call_type,
        cover_image: coverPath,
      };
      const res = await fetch(`${API_URL}/api/calls/group-events`, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        setShowCreateSession(false);
        setSessionForm({ title: '', description: '', topic: '', date: '', time_from: '10:00', time_to: '11:00', max_participants: '', price: '', call_type: 'video', cover_image: '' });
        setCoverFile(null); setCoverPreview(null);
      }
    } catch {}
    setCreatingSession(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl border border-zinc-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="monetization-modal">
        <div className="sticky top-0 bg-zinc-900 z-10 px-5 pt-5 pb-3 border-b border-zinc-800">
          <div className="flex items-center justify-between"><h3 className="font-bold text-lg">Monetization</h3><button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button></div>
          <p className="text-xs text-zinc-500 mt-1">Toggle each method on/off and set your prices</p>
        </div>
        <div className="p-5 space-y-3">
          {/* Currency Selector */}
          <div className="bg-zinc-800 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white">Currency</p>
              <p className="text-[10px] text-zinc-500">Set your pricing currency</p>
            </div>
            <select value={currency} onChange={e => setCurrency(e.target.value)}
              className="bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-1.5 text-sm outline-none text-white"
              data-testid="currency-selector">
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {methods.map(m => (
            <div key={m.key} className={`bg-zinc-800 rounded-xl p-4 ${!toggles[`monetization_${m.key}`] ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2"><m.icon className={`w-5 h-5 ${m.color}`} /><span className="text-sm font-semibold">{m.label}</span></div>
                <button onClick={() => setToggles({ ...toggles, [`monetization_${m.key}`]: !toggles[`monetization_${m.key}`] })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${toggles[`monetization_${m.key}`] ? 'bg-white' : 'bg-zinc-600'}`} data-testid={`toggle-${m.key}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-transform ${toggles[`monetization_${m.key}`] ? 'translate-x-5 bg-black' : 'translate-x-0.5 bg-zinc-400'}`} />
                </button>
              </div>
              {toggles[`monetization_${m.key}`] && (
                <div className="flex items-center space-x-2"><span className="text-xs text-zinc-400" data-testid="price-currency-symbol">{getCurrencySymbol(currency)}</span>
                  <input type="number" min="0" step="0.5" value={prices[`price_${m.key}`]} onChange={e => setPrices({ ...prices, [`price_${m.key}`]: parseFloat(e.target.value) || 0 })}
                    className="w-24 bg-zinc-700 rounded-lg px-3 py-1.5 text-sm outline-none" data-testid={`price-${m.key}`} />
                </div>
              )}
              {/* New Session button under Group Sessions */}
              {m.key === 'group_session' && toggles.monetization_group_session && (
                <div className="mt-3 border-t border-zinc-700 pt-3">
                  <button onClick={() => setShowCreateSession(!showCreateSession)}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl py-2.5 text-xs flex items-center justify-center gap-1.5 transition-colors"
                    data-testid="new-session-btn">
                    <Plus className="w-3.5 h-3.5" /> New Session
                  </button>
                  {showCreateSession && (
                    <div className="mt-3 space-y-2.5 bg-zinc-900 rounded-xl p-3 border border-zinc-700" data-testid="create-session-form">
                      <input value={sessionForm.title} onChange={e => setSessionForm({ ...sessionForm, title: e.target.value })}
                        placeholder="Session Title" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none placeholder-zinc-600" data-testid="session-title" />
                      <input value={sessionForm.topic} onChange={e => setSessionForm({ ...sessionForm, topic: e.target.value })}
                        placeholder="Topic (shown to users)" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none placeholder-zinc-600" data-testid="session-topic" />
                      <textarea value={sessionForm.description} onChange={e => setSessionForm({ ...sessionForm, description: e.target.value })}
                        placeholder="Write about this session..." rows={2}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none placeholder-zinc-600 resize-none" data-testid="session-desc" />
                      {/* Cover Upload */}
                      <div>
                        <label className="text-[10px] text-zinc-500 mb-1 block">Cover Photo/Video</label>
                        {coverPreview ? (
                          <div className="relative rounded-xl overflow-hidden border border-zinc-700 h-28">
                            <img src={coverPreview} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => { setCoverFile(null); setCoverPreview(null); }}
                              className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
                          </div>
                        ) : (
                          <label className="w-full h-20 border-2 border-dashed border-zinc-700 rounded-xl flex items-center justify-center cursor-pointer hover:border-purple-500/50 transition-colors">
                            <input type="file" accept="image/*,video/*" className="hidden" onChange={handleCoverChange} data-testid="session-cover-input" />
                            <span className="text-xs text-zinc-600">Tap to upload</span>
                          </label>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-zinc-500 mb-1 block">Date</label>
                          <input type="date" value={sessionForm.date} onChange={e => setSessionForm({ ...sessionForm, date: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="session-date" />
                        </div>
                        <div>
                          <label className="text-[10px] text-zinc-500 mb-1 block">Call Type</label>
                          <div className="flex gap-1.5">
                            {['video', 'voice'].map(t => (
                              <button key={t} onClick={() => setSessionForm({ ...sessionForm, call_type: t })}
                                className={`flex-1 py-2 rounded-xl text-[11px] font-semibold transition-all ${sessionForm.call_type === t ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}>
                                {t === 'video' ? 'Video' : 'Voice'}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      {/* Time: From - To */}
                      <div>
                        <label className="text-[10px] text-zinc-500 mb-1 block">Time</label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <span className="text-[9px] text-zinc-600 mb-0.5 block">From</span>
                            <input type="time" value={sessionForm.time_from} onChange={e => setSessionForm({ ...sessionForm, time_from: e.target.value })}
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="session-time-from" />
                          </div>
                          <span className="text-zinc-600 text-xs mt-4">to</span>
                          <div className="flex-1">
                            <span className="text-[9px] text-zinc-600 mb-0.5 block">To</span>
                            <input type="time" value={sessionForm.time_to} onChange={e => setSessionForm({ ...sessionForm, time_to: e.target.value })}
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-sm outline-none text-white" data-testid="session-time-to" />
                          </div>
                        </div>
                        {sessionForm.time_from && sessionForm.time_to && (() => {
                          const [fh, fm] = sessionForm.time_from.split(':').map(Number);
                          const [th, tm] = sessionForm.time_to.split(':').map(Number);
                          const mins = (th * 60 + tm) - (fh * 60 + fm);
                          if (mins > 0) return <p className="text-[9px] text-zinc-600 mt-1">{Math.floor(mins / 60)}h {mins % 60 > 0 ? `${mins % 60}m` : ''} session</p>;
                          return null;
                        })()}
                      </div>
                      {/* Max People */}
                      <div>
                        <label className="text-[10px] text-zinc-500 mb-1 block">Max People</label>
                        <input type="text" inputMode="numeric" value={sessionForm.max_participants}
                          onChange={e => setSessionForm({ ...sessionForm, max_participants: e.target.value.replace(/[^0-9]/g, '') })}
                          placeholder="e.g. 20"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none text-white placeholder-zinc-600" data-testid="session-max" />
                      </div>
                      {/* Price */}
                      <div>
                        <label className="text-[10px] text-zinc-500 mb-1 block">Price per person ({getCurrencySymbol(currency)})</label>
                        <input type="text" inputMode="decimal" value={sessionForm.price}
                          onChange={e => setSessionForm({ ...sessionForm, price: e.target.value.replace(/[^0-9.]/g, '') })}
                          placeholder="e.g. 149 (0 for free)"
                          className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm outline-none text-white placeholder-zinc-600" data-testid="session-price" />
                      </div>
                      <button onClick={createGroupSession} disabled={creatingSession || !sessionForm.title || !sessionForm.date}
                        className="w-full py-2.5 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl text-xs transition-colors disabled:opacity-50"
                        data-testid="submit-session-btn">
                        {creatingSession ? 'Creating...' : 'Create Group Session'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="sticky bottom-0 bg-zinc-900 px-5 pb-5 pt-3 border-t border-zinc-800">
          <button onClick={save} disabled={saving} className="w-full bg-white text-black font-bold rounded-full py-3 text-sm disabled:opacity-50" data-testid="save-monetization-btn">{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

function AddExpertiseModal({ onClose, onAdded }) {
  const [taxonomy, setTaxonomy] = useState({});
  const [industry, setIndustry] = useState('');
  const [customIndustry, setCustomIndustry] = useState('');
  const [expertise, setExpertise] = useState('');
  const [customExpertise, setCustomExpertise] = useState('');
  const [sub, setSub] = useState('');
  const [customSub, setCustomSub] = useState('');
  const [micro, setMicro] = useState('');
  const [customMicro, setCustomMicro] = useState('');
  const [level, setLevel] = useState('intermediate');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  useEffect(() => { fetch(`${API_URL}/api/taxonomy`).then(r => r.json()).then(setTaxonomy).catch(() => {}); }, []);
  const getChildren = (obj) => Object.keys(obj?.children || {});

  const finalIndustry = industry === '__custom__' ? customIndustry : industry;
  const finalExpertise = expertise === '__custom__' ? customExpertise : expertise;
  const finalSub = sub === '__custom__' ? customSub : sub;
  const finalMicro = micro === '__custom__' ? customMicro : micro;

  const save = async () => {
    if (!finalIndustry || !finalExpertise) return;
    setSaving(true);
    const res = await fetch(`${API_URL}/api/expertise/add`, {
      method: 'POST', headers,
      body: JSON.stringify({ industry: finalIndustry, expertise: finalExpertise, subcategory: finalSub || null, microniche: finalMicro || null, skill_level: level }),
    });
    if (res.ok) {
      // Auto-create chatroom for custom subjects
      if (industry === '__custom__' || expertise === '__custom__' || sub === '__custom__' || micro === '__custom__') {
        const subjectName = finalMicro || finalSub || finalExpertise;
        const subjectPath = `${finalIndustry} > ${finalExpertise}${finalSub ? ` > ${finalSub}` : ''}${finalMicro ? ` > ${finalMicro}` : ''}`;
        try {
          await fetch(`${API_URL}/api/chatrooms/custom?name=${encodeURIComponent(subjectName)}&subject_path=${encodeURIComponent(subjectPath)}`, { method: 'POST', headers });
        } catch {}
      }
      onAdded();
    } else { const e = await res.json(); alert(e.detail || 'Error'); }
    setSaving(false);
  };

  const selectCls = "w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-3 text-sm outline-none focus:border-purple-500/50 appearance-none";
  const inputCls = "w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-3 text-sm outline-none focus:border-purple-500/50 placeholder-zinc-500 mt-2";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-5 border border-zinc-800 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-expertise-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg">Add Expertise</h3><button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button></div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-400 mb-1 font-medium">Industry</label>
            <select value={industry} onChange={e => { setIndustry(e.target.value); setExpertise(''); setSub(''); setMicro(''); setCustomIndustry(''); }} className={selectCls} data-testid="select-industry">
              <option value="">Select...</option>
              {Object.keys(taxonomy).map(i => <option key={i} value={i}>{i}</option>)}
              <option value="__custom__">Other (type your own)</option>
            </select>
            {industry === '__custom__' && <input type="text" value={customIndustry} onChange={e => setCustomIndustry(e.target.value)} placeholder="Enter custom industry..." className={inputCls} data-testid="custom-industry-input" />}
          </div>

          {(finalIndustry) && <div>
            <label className="block text-xs text-zinc-400 mb-1 font-medium">Expertise / Subject</label>
            <select value={expertise} onChange={e => { setExpertise(e.target.value); setSub(''); setMicro(''); setCustomExpertise(''); }} className={selectCls} data-testid="select-expertise">
              <option value="">Select...</option>
              {industry !== '__custom__' && getChildren(taxonomy[industry]).map(e => <option key={e} value={e}>{e}</option>)}
              <option value="__custom__">Other (type your own)</option>
            </select>
            {expertise === '__custom__' && <input type="text" value={customExpertise} onChange={e => setCustomExpertise(e.target.value)} placeholder="Enter custom subject..." className={inputCls} data-testid="custom-expertise-input" />}
          </div>}

          {finalExpertise && industry !== '__custom__' && taxonomy[industry]?.children?.[expertise]?.children && <div>
            <label className="block text-xs text-zinc-400 mb-1 font-medium">Sub-Category</label>
            <select value={sub} onChange={e => { setSub(e.target.value); setMicro(''); setCustomSub(''); }} className={selectCls} data-testid="select-sub">
              <option value="">Select...</option>
              {expertise !== '__custom__' && getChildren(taxonomy[industry]?.children?.[expertise] || {}).map(s => <option key={s} value={s}>{s}</option>)}
              <option value="__custom__">Other (type your own)</option>
            </select>
            {sub === '__custom__' && <input type="text" value={customSub} onChange={e => setCustomSub(e.target.value)} placeholder="Enter custom sub-category..." className={inputCls} data-testid="custom-sub-input" />}
          </div>}

          {finalSub && industry !== '__custom__' && expertise !== '__custom__' && taxonomy[industry]?.children?.[expertise]?.children?.[sub]?.children && Object.keys(taxonomy[industry]?.children?.[expertise]?.children?.[sub]?.children || {}).length > 0 && <div>
            <label className="block text-xs text-zinc-400 mb-1 font-medium">Micro-Niche</label>
            <select value={micro} onChange={e => { setMicro(e.target.value); setCustomMicro(''); }} className={selectCls} data-testid="select-micro">
              <option value="">Select...</option>
              {sub !== '__custom__' && getChildren(taxonomy[industry]?.children?.[expertise]?.children?.[sub] || {}).map(m => <option key={m} value={m}>{m}</option>)}
              <option value="__custom__">Other (type your own)</option>
            </select>
            {micro === '__custom__' && <input type="text" value={customMicro} onChange={e => setCustomMicro(e.target.value)} placeholder="Enter custom micro-niche..." className={inputCls} data-testid="custom-micro-input" />}
          </div>}

          <div><label className="block text-xs text-zinc-400 mb-1 font-medium">Skill Level</label>
            <div className="flex space-x-2">{SKILL_LEVELS.map(l => (
              <button key={l.value} onClick={() => setLevel(l.value)} className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${level === l.value ? 'bg-white text-black shadow-lg' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`} data-testid={`level-${l.value}`}>{l.label}</button>
            ))}</div></div>
        </div>
        <button onClick={save} disabled={saving || !finalIndustry || !finalExpertise} className="w-full bg-white text-black font-bold rounded-full py-3.5 text-sm mt-4 disabled:opacity-30 hover:bg-zinc-100 transition-colors" data-testid="save-expertise-btn">{saving ? 'Adding...' : 'Add Expertise'}</button>
      </div>
    </div>
  );
}


function PeerVerifyModal({ onClose }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/verification/queue`, { headers }).then(r => r.json()).then(d => { setQueue(d.queue || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const vote = async (reviewId, approve) => {
    setVoting(reviewId);
    const res = await fetch(`${API_URL}/api/verification/${reviewId}/vote?approve=${approve}`, { method: 'POST', headers });
    if (res.ok) {
      const d = await res.json();
      setQueue(prev => prev.filter(r => r.review_id !== reviewId));
      alert(d.message);
    }
    setVoting(null);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl p-5 border border-zinc-800 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="verify-queue-modal">
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg">Peer Review Queue</h3><button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button></div>
        <p className="text-xs text-zinc-500 mb-4">Review expertise claims in your field. 3 approvals = peer verified.</p>
        {loading ? <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /></div> :
          queue.length === 0 ? <p className="text-center py-8 text-zinc-500 text-sm">No pending reviews in your field</p> :
          queue.map(r => (
            <div key={r.review_id} className="bg-zinc-800 rounded-xl p-4 mb-3">
              <p className="font-semibold text-sm">{r.user_name}</p>
              <p className="text-xs text-zinc-400 mt-1">{r.branch?.industry} / {r.branch?.expertise}{r.branch?.subcategory ? ` / ${r.branch.subcategory}` : ''}</p>
              <p className="text-[10px] text-zinc-500 mt-1">Skill level: {r.branch?.skill_level || 'intermediate'}</p>
              <div className="flex space-x-2 mt-3">
                <button onClick={() => vote(r.review_id, true)} disabled={voting === r.review_id}
                  className="flex-1 bg-green-500/15 text-green-400 font-bold rounded-lg py-2 text-xs hover:bg-green-500/25" data-testid={`approve-${r.review_id}`}>Approve</button>
                <button onClick={() => vote(r.review_id, false)} disabled={voting === r.review_id}
                  className="flex-1 bg-red-500/15 text-red-400 font-bold rounded-lg py-2 text-xs hover:bg-red-500/25" data-testid={`reject-${r.review_id}`}>Reject</button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}


// ═══ ENRICHED PROFILE FIELDS COMPONENT ═══
function EnrichedProfileSection({ displayUser, isOwn, headers, showToast, onRefresh }) {
  const [editing, setEditing] = useState(null); // 'experiences' | 'education' | 'projects' | 'publications' | etc.
  const [formData, setFormData] = useState({});

  const sections = [
    { key: 'experiences', label: 'Work Experience', fields: ['title', 'company', 'start_date', 'end_date', 'description'], icon: '💼' },
    { key: 'education_history', label: 'Education', fields: ['institution', 'degree', 'field', 'start_year', 'end_year'], icon: '🎓' },
    { key: 'projects', label: 'Projects', fields: ['name', 'description', 'url'], icon: '📁' },
    { key: 'publications', label: 'Publications', fields: ['title', 'publisher', 'year', 'url'], icon: '📄' },
    { key: 'achievements', label: 'Achievements', fields: ['title', 'year', 'description'], icon: '🏆' },
  ];

  const saveField = async (key, value) => {
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: value }),
      });
      if (res.ok) { showToast?.('Profile updated!'); onRefresh?.(); setEditing(null); setFormData({}); }
    } catch {}
  };

  const addItem = (key) => {
    const current = displayUser?.[key] || [];
    const newItem = {};
    const sec = sections.find(s => s.key === key);
    if (sec) sec.fields.forEach(f => { newItem[f] = formData[f] || ''; });
    saveField(key, [...current, newItem]);
  };

  const removeItem = (key, idx) => {
    const current = [...(displayUser?.[key] || [])];
    current.splice(idx, 1);
    saveField(key, current);
  };

  return (
    <div data-testid="enriched-profile">
      {/* Open To Tags */}
      {(displayUser?.open_to?.length > 0 || isOwn) && (
        <div className="px-4 py-3 border-t border-zinc-800" data-testid="open-to-section">
          <p className="text-xs text-zinc-400 font-semibold mb-2 uppercase tracking-wide">Open To</p>
          <div className="flex flex-wrap gap-1.5">
            {(displayUser?.open_to || []).map((tag, i) => (
              <span key={i} className="px-2.5 py-1 bg-green-500/10 text-green-400 text-[10px] font-medium rounded-full border border-green-500/20">
                {tag}
                {isOwn && <button onClick={() => { const updated = (displayUser.open_to || []).filter((_, idx) => idx !== i); saveField('open_to', updated); }} className="ml-1 text-green-400/60 hover:text-red-400">×</button>}
              </span>
            ))}
            {isOwn && (
              <button onClick={() => {
                const tag = prompt('Add an "Open To" tag (e.g. Mentoring, Collaboration, Freelance)');
                if (tag) saveField('open_to', [...(displayUser?.open_to || []), tag.trim()]);
              }} className="px-3.5 py-1.5 bg-zinc-800 text-zinc-300 text-xs font-medium rounded-full border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-700 transition-colors" data-testid="add-open-to">
                + Add
              </button>
            )}
          </div>
        </div>
      )}

      {/* Languages Proficiency */}
      {(displayUser?.languages_proficiency?.length > 0 || isOwn) && (
        <div className="px-4 py-3 border-t border-zinc-800" data-testid="languages-section">
          <p className="text-xs text-zinc-400 font-semibold mb-2 uppercase tracking-wide">Languages</p>
          <div className="flex flex-wrap gap-1.5">
            {(displayUser?.languages_proficiency || []).map((lang, i) => (
              <span key={i} className="px-2.5 py-1 bg-blue-500/10 text-blue-400 text-[10px] font-medium rounded-full border border-blue-500/20">
                {typeof lang === 'string' ? lang : `${lang.language} (${lang.level || 'fluent'})`}
                {isOwn && <button onClick={() => { const updated = (displayUser.languages_proficiency || []).filter((_, idx) => idx !== i); saveField('languages_proficiency', updated); }} className="ml-1 text-blue-400/60 hover:text-red-400">×</button>}
              </span>
            ))}
            {isOwn && (
              <button onClick={() => {
                const lang = prompt('Add language (e.g. English, Hindi, Spanish)');
                if (lang) saveField('languages_proficiency', [...(displayUser?.languages_proficiency || []), { language: lang.trim(), level: 'fluent' }]);
              }} className="px-3.5 py-1.5 bg-zinc-800 text-zinc-300 text-xs font-medium rounded-full border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-700 transition-colors" data-testid="add-language">
                + Add
              </button>
            )}
          </div>
        </div>
      )}

      {/* Structured sections: Experience, Education, Projects, Publications, Achievements */}
      {sections.map(sec => {
        const items = displayUser?.[sec.key] || [];
        if (items.length === 0 && !isOwn) return null;
        return (
          <div key={sec.key} className="px-4 py-3 border-t border-zinc-800" data-testid={`section-${sec.key}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-zinc-400 font-semibold uppercase tracking-wide">{sec.icon} {sec.label}</p>
              {isOwn && (
                <button onClick={() => { setEditing(editing === sec.key ? null : sec.key); setFormData({}); }}
                  className="px-3 py-1.5 bg-zinc-800 text-zinc-300 text-xs font-medium rounded-lg border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-700 transition-colors" data-testid={`add-${sec.key}`}>
                  {editing === sec.key ? 'Cancel' : '+ Add'}
                </button>
              )}
            </div>
            {/* Existing items */}
            {items.map((item, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 mb-2 group relative">
                <p className="text-xs font-semibold text-white">{item.title || item.name || item.institution || item.company || 'Untitled'}</p>
                {(item.company || item.degree || item.publisher) && <p className="text-[10px] text-zinc-400">{item.company || item.degree || item.publisher}{item.field ? ` — ${item.field}` : ''}</p>}
                {(item.start_date || item.start_year) && <p className="text-[9px] text-zinc-500">{item.start_date || item.start_year} — {item.end_date || item.end_year || 'Present'}</p>}
                {item.description && <p className="text-[10px] text-zinc-400 mt-1 line-clamp-2">{item.description}</p>}
                {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-purple-400 hover:underline mt-1 block">{item.url}</a>}
                {isOwn && (
                  <button onClick={() => removeItem(sec.key, i)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-red-500/20 rounded-full transition-opacity" data-testid={`remove-${sec.key}-${i}`}>
                    <X className="w-3 h-3 text-red-400" />
                  </button>
                )}
              </div>
            ))}
            {/* Add form */}
            {editing === sec.key && (
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 space-y-2" data-testid={`form-${sec.key}`}>
                {sec.fields.map(f => (
                  <input key={f} type="text" placeholder={f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    value={formData[f] || ''} onChange={e => setFormData(p => ({ ...p, [f]: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600"
                  />
                ))}
                <button onClick={() => addItem(sec.key)}
                  className="w-full py-2 bg-white text-black text-xs font-bold rounded-lg hover:bg-zinc-200 transition-colors" data-testid={`save-${sec.key}`}>
                  Save {sec.label}
                </button>
              </div>
            )}
            {items.length === 0 && !editing && isOwn && (
              <p className="text-[10px] text-zinc-600">Add your {sec.label.toLowerCase()} to enrich your profile</p>
            )}
          </div>
        );
      })}

      {/* Teaching Style */}
      {(displayUser?.teaching_style || isOwn) && (
        <div className="px-4 py-3 border-t border-zinc-800" data-testid="teaching-style-section">
          <p className="text-xs text-zinc-400 font-semibold mb-1 uppercase tracking-wide">Teaching Style</p>
          {displayUser?.teaching_style ? (
            <p className="text-xs text-zinc-300">{displayUser.teaching_style}</p>
          ) : isOwn ? (
            <button onClick={() => {
              const style = prompt('Describe your teaching/session style');
              if (style) saveField('teaching_style', style.trim());
            }} className="text-xs text-purple-400 hover:text-purple-300 font-medium bg-purple-500/10 px-3 py-1.5 rounded-lg" data-testid="add-teaching-style">+ Add teaching style</button>
          ) : null}
        </div>
      )}

      {/* Fun Fact */}
      {(displayUser?.fun_fact || isOwn) && (
        <div className="px-4 py-3 border-t border-zinc-800" data-testid="fun-fact-section">
          <p className="text-xs text-zinc-400 font-semibold mb-1 uppercase tracking-wide">Fun Fact</p>
          {displayUser?.fun_fact ? (
            <p className="text-xs text-zinc-300">{displayUser.fun_fact}</p>
          ) : isOwn ? (
            <button onClick={() => {
              const fact = prompt('Share a fun fact about yourself');
              if (fact) saveField('fun_fact', fact.trim());
            }} className="text-xs text-purple-400 hover:text-purple-300 font-medium bg-purple-500/10 px-3 py-1.5 rounded-lg" data-testid="add-fun-fact">+ Add fun fact</button>
          ) : null}
        </div>
      )}
    </div>
  );
}


// ═══ SCHEDULE MODAL ═══
function ScheduleModal({ user, displayUser, onClose, headers, showToast, refreshUser }) {
  const [bookingMode, setBookingMode] = useState(displayUser?.booking_mode || 'instant');
  const [availability, setAvailability] = useState({});
  const [timezone, setTimezone] = useState(user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scheduleType, setScheduleType] = useState(user?.schedule_type || 'weekly');
  const token = localStorage.getItem('token');

  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // Full 24-hour availability starting from 6 AM through next-day 5 AM
  const HOURS = [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,0,1,2,3,4,5];

  useEffect(() => { fetchAvailability(); }, []);

  const fetchAvailability = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/availability/${user.user_id}`);
      if (res.ok) {
        const data = await res.json();
        const grid = {};
        (data.slots || []).forEach(s => { grid[`${s.day}-${s.hour}`] = true; });
        setAvailability(grid);
      }
    } catch {}
    setLoading(false);
  };

  const toggleSlot = (day, hour) => {
    const key = `${day}-${hour}`;
    setAvailability(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleBookingMode = async (mode) => {
    setBookingMode(mode);
    try {
      await fetch(`${API_URL}/api/profile/booking-mode?mode=${mode}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch {}
  };

  const saveSchedule = async () => {
    setSaving(true);
    const slots = Object.entries(availability).filter(([_, v]) => v).map(([k]) => {
      const [day, hour] = k.split('-');
      return { day, hour: parseInt(hour), timezone };
    });
    try {
      const res = await fetch(`${API_URL}/api/calendar/availability`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slots, timezone, schedule_type: scheduleType }),
      });
      if (res.ok) {
        showToast?.('Schedule saved');
        refreshUser?.();
      }
    } catch {}
    setSaving(false);
  };

  const formatHour = (h) => {
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-zinc-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl border border-zinc-800 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="schedule-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 shrink-0">
          <h3 className="font-bold text-lg">My Schedule</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-6">
          {/* Section A — Booking Mode */}
          <div data-testid="booking-mode-section-modal">
            <p className="text-xs font-bold text-white uppercase tracking-wide mb-3">How do you accept bookings?</p>
            <div className="flex rounded-xl bg-zinc-800 border border-zinc-700 p-1 mb-2">
              <button onClick={() => toggleBookingMode('instant')}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${bookingMode === 'instant' ? 'bg-white text-black' : 'text-zinc-400'}`}
                data-testid="mode-instant">
                Auto-accept
              </button>
              <button onClick={() => toggleBookingMode('request')}
                className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${bookingMode === 'request' ? 'bg-white text-black' : 'text-zinc-400'}`}
                data-testid="mode-request">
                Request first
              </button>
            </div>
            <p className="text-[10px] text-zinc-500">
              {bookingMode === 'instant'
                ? 'Learners can book you instantly. You\'ll be notified immediately.'
                : 'You review each request before it\'s confirmed. No charge until you accept.'}
            </p>
          </div>

          {/* Section B — Schedule Type removed per user preference; defaults to weekly */}

          {/* Section C — Timezone */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-white uppercase tracking-wide">Weekly Availability</p>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-[10px] text-zinc-300 outline-none" data-testid="timezone-selector">
                {['Asia/Kolkata', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Tokyo', 'Australia/Sydney', 'Asia/Dubai'].map(tz => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            {/* Calendar Grid */}
            {loading ? (
              <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="overflow-x-auto">
                <div className="inline-grid" style={{ gridTemplateColumns: `50px repeat(7, 42px)` }}>
                  {/* Header row */}
                  <div />
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-[9px] font-bold text-zinc-400 py-1">{d}</div>
                  ))}
                  {/* Hour rows — 6 AM through next-day 5 AM (24 hours) */}
                  {HOURS.map(h => (
                    <React.Fragment key={h}>
                      <div className="text-[9px] text-zinc-500 text-right pr-1 py-0.5 leading-[22px]">{formatHour(h)}</div>
                      {DAYS.map(d => {
                        const key = `${d}-${h}`;
                        const active = availability[key];
                        return (
                          <button key={key} onClick={() => toggleSlot(d, h)}
                            className={`w-[38px] h-[22px] mx-0.5 my-0.5 rounded transition-all ${active ? 'bg-emerald-500/40 border border-emerald-500/60' : 'bg-zinc-800/50 border border-zinc-700/30 hover:bg-zinc-700/50'}`}
                            data-testid={`slot-${d}-${h}`} />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Section C — Save */}
          <button onClick={saveSchedule} disabled={saving}
            className="w-full bg-white text-black font-bold rounded-xl py-3 text-sm disabled:opacity-50 hover:bg-zinc-200 transition-colors" data-testid="save-schedule-btn">
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ TYPEAHEAD INPUT COMPONENT ═══
function TypeaheadInput({ value, onChange, field, placeholder, className, 'data-testid': testId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const debounceRef = useRef(null);

  useEffect(() => { setLocalValue(value || ''); }, [value]);

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/autocomplete/fields?field=${encodeURIComponent(field)}&q=${encodeURIComponent(q)}&limit=8`);
      if (res.ok) {
        const d = await res.json();
        setSuggestions(d.suggestions || []);
        setShowSuggestions((d.suggestions || []).length > 0);
      }
    } catch {}
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setLocalValue(v);
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  };

  const selectSuggestion = (s) => {
    setLocalValue(s);
    onChange?.(s);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 bg-zinc-800 border border-zinc-700 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-xl" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => selectSuggestion(s)}
              className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors border-b border-zinc-700/50 last:border-0"
              data-testid={`suggestion-${i}`}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══ PROFILE COMPLETENESS RING ═══
function ProfileCompletenessRing({ user }) {
  if (!user) return null;
  const checks = [
    { label: 'Name', filled: !!user.name },
    { label: 'Headline', filled: !!user.headline },
    { label: 'Bio', filled: !!user.bio },
    { label: 'Photo', filled: !!user.picture },
    { label: 'City', filled: !!user.city },
    { label: 'Position', filled: !!user.position },
    { label: 'Company', filled: !!user.company },
    { label: 'Education', filled: !!user.education },
    { label: 'Experience', filled: (user.experiences || []).length > 0 },
    { label: 'Skills', filled: (user.expertise_tree || []).length > 0 },
    { label: 'Open To', filled: (user.open_to || []).length > 0 },
    { label: 'Languages', filled: (user.languages_proficiency || []).length > 0 },
  ];
  const filled = checks.filter(c => c.filled).length;
  const total = checks.length;
  const pct = Math.round((filled / total) * 100);
  const missing = checks.filter(c => !c.filled).slice(0, 3);

  if (pct === 100) return null;

  const circumference = 2 * Math.PI * 18;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <div className="mt-3 bg-zinc-900/50 border border-zinc-800 rounded-xl px-3 py-2.5 flex items-center gap-3" data-testid="completeness-ring">
      <div className="relative w-11 h-11 shrink-0">
        <svg className="w-11 h-11 -rotate-90" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="18" fill="none" stroke="#27272a" strokeWidth="3" />
          <circle cx="20" cy="20" r="18" fill="none" stroke={pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#8b5cf6'}
            strokeWidth="3" strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" className="transition-all duration-700" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{pct}%</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-white">Profile {pct}% complete</p>
        {missing.length > 0 && (
          <p className="text-[9px] text-zinc-500 mt-0.5 truncate">Add: {missing.map(m => m.label).join(', ')}</p>
        )}
      </div>
    </div>
  );
}

// ═══ VERIFICATION BADGE SECTION ═══
function VerificationBadgeSection({ user, token, showToast, refreshUser }) {
  const [uploading, setUploading] = useState(false);
  const status = user?.identity_verification_status || 'none';

  if (status === 'verified') return null; // Already verified, blue tick shows on name

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/verify/identity`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        showToast?.('Document submitted for verification');
        refreshUser?.();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast?.(err.detail || 'Upload failed');
      }
    } catch {}
    setUploading(false);
  };

  return (
    <div className="mt-2 bg-zinc-900/50 border border-zinc-800 rounded-xl px-3 py-2.5 flex items-center gap-3" data-testid="verification-section">
      <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
        <svg className="w-4.5 h-4.5 text-blue-500" viewBox="0 0 22 22" fill="currentColor">
          <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.855-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.69-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.636.433 1.221.878 1.69.47.446 1.055.752 1.69.883.635.13 1.294.083 1.902-.141.27.587.7 1.086 1.24 1.44s1.167.551 1.813.568c.645-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.224 1.26.273 1.896.143.636-.13 1.22-.436 1.69-.88.445-.47.75-1.055.88-1.69.13-.634.08-1.29-.14-1.898.587-.273 1.084-.704 1.438-1.244.355-.54.552-1.17.57-1.817zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z"/>
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        {status === 'pending' ? (
          <>
            <p className="text-[11px] font-semibold text-yellow-400">Verification Pending</p>
            <p className="text-[9px] text-zinc-500">Your ID is being reviewed</p>
          </>
        ) : (
          <>
            <p className="text-[11px] font-semibold text-white">Get Verified</p>
            <p className="text-[9px] text-zinc-500">Upload Aadhaar/ID for blue tick</p>
          </>
        )}
      </div>
      {status !== 'pending' && (
        <label className="shrink-0 px-3 py-1.5 bg-blue-500/15 text-blue-400 text-[10px] font-bold rounded-lg cursor-pointer hover:bg-blue-500/25 transition-colors" data-testid="upload-aadhaar-btn">
          {uploading ? '...' : 'Upload ID'}
          <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleUpload} />
        </label>
      )}
    </div>
  );
}


// ═══ BOOKING FLOW MODAL ═══
function BookingFlowModal({ expert, user, onClose, showToast, initialCallType }) {
  const [step, setStep] = useState(1); // 1=calendar, 2=time/duration, 3=refund+pay, 4=status
  const [availability, setAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [callType, setCallType] = useState(initialCallType || 'video');
  const [duration, setDuration] = useState(15);
  const [bookingResult, setBookingResult] = useState(null);
  const [briefing, setBriefing] = useState('');
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const DURATIONS = [15, 30, 45, 60];

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/availability/${expert.user_id}`);
      if (res.ok) {
        const data = await res.json();
        setAvailability(data.slots || []);
      }
    } catch {}
    setLoading(false);
  };

  // Build next 14 days calendar
  const DAYS_MAP = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };
  const dates = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

  const getAvailableHours = (date) => {
    const dayName = DAYS_MAP[date.getDay()];
    return availability.filter(s => s.day === dayName).map(s => s.hour).sort((a, b) => a - b);
  };

  const formatHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:00 ${ampm}`;
  };

  const basePrice = callType === 'video' ? (expert.price_video_call || 75) : (expert.price_voice_call || 50);
  const pricePerMin = basePrice / 30;
  const totalPrice = Math.round(pricePerMin * duration);

  const handlePayRazorpay = async () => {
    if (!selectedDate || selectedSlot === null) return;
    setPaying(true);
    const slotDate = selectedDate.toISOString().split('T')[0];
    const slotTime = formatHour(selectedSlot);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST', headers,
        body: JSON.stringify({ expert_id: expert.user_id, payment_type: callType === 'video' ? 'video_call' : 'voice_call', amount_override: totalPrice }),
      });
      if (res.ok) {
        const d = await res.json();
        const rzpResponse = await openRazorpayCheckout({
          order_id: d.order_id, amount: d.amount, currency: d.currency,
          key_id: d.key_id, name: 'Connect',
          description: `${duration}min ${callType} call with ${expert.name}`,
          prefill: { name: user?.name, email: user?.email },
        });
        // Verify payment
        const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
          method: 'POST', headers,
          body: JSON.stringify({ ...rzpResponse, txn_id: d.txn_id }),
        });
        if (verifyRes.ok) {
          // Now book the call
          const bookRes = await fetch(`${API_URL}/api/book/call?expert_id=${expert.user_id}&call_type=${callType}&slot_date=${slotDate}&slot_time=${slotTime}&briefing=${encodeURIComponent(briefing)}`, {
            method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
          });
          if (bookRes.ok) {
            const booking = await bookRes.json();
            setBookingResult(booking);
            setStep(4);
            showToast?.('Booking confirmed!');
          }
        }
      }
    } catch (e) {
      if (e.message !== 'Payment cancelled') showToast?.(e.message || 'Payment failed');
    }
    setPaying(false);
  };

  const handlePayWallet = async () => {
    if (!selectedDate || selectedSlot === null) return;
    setPaying(true);
    const slotDate = selectedDate.toISOString().split('T')[0];
    const slotTime = formatHour(selectedSlot);
    try {
      const res = await fetch(`${API_URL}/api/wallet/pay`, {
        method: 'POST', headers,
        body: JSON.stringify({ expert_id: expert.user_id, payment_type: callType === 'video' ? 'video_call' : 'voice_call' }),
      });
      if (res.ok) {
        const bookRes = await fetch(`${API_URL}/api/book/call?expert_id=${expert.user_id}&call_type=${callType}&slot_date=${slotDate}&slot_time=${slotTime}&briefing=${encodeURIComponent(briefing)}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
        });
        if (bookRes.ok) {
          const booking = await bookRes.json();
          setBookingResult(booking);
          setStep(4);
          showToast?.('Booking confirmed!');
        }
      } else {
        const err = await res.json();
        showToast?.(err.detail || 'Wallet payment failed');
      }
    } catch {}
    setPaying(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="booking-flow-modal">
      <div className="bg-zinc-900 w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-zinc-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-2">
            {step > 1 && step < 4 && (
              <button onClick={() => setStep(step - 1)} className="text-zinc-400 hover:text-white" data-testid="booking-back"><X className="w-4 h-4 rotate-45" /></button>
            )}
            <h3 className="font-bold text-white text-sm">
              {step === 1 && 'Select Date'}
              {step === 2 && 'Choose Time & Duration'}
              {step === 3 && 'Review & Pay'}
              {step === 4 && 'Booking Status'}
            </h3>
          </div>
          <button onClick={onClose} data-testid="booking-close"><X className="w-5 h-5 text-zinc-400" /></button>
        </div>
        {/* Progress */}
        {step < 4 && (
          <div className="flex gap-1 px-4 pt-3">
            {[1,2,3].map(s => (
              <div key={s} className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-purple-500' : 'bg-zinc-800'}`} />
            ))}
          </div>
        )}

        {/* Step 1: Calendar */}
        {step === 1 && (
          <div className="p-4" data-testid="booking-step-calendar">
            {loading ? (
              <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <>
                <p className="text-xs text-zinc-500 mb-3">Pick a date with {expert.name}</p>
                <div className="grid grid-cols-7 gap-1.5">
                  {dates.map(d => {
                    const hours = getAvailableHours(d);
                    const available = hours.length > 0;
                    const selected = selectedDate?.toDateString() === d.toDateString();
                    return (
                      <button key={d.toISOString()} onClick={() => { if (available) { setSelectedDate(d); setSelectedSlot(null); } }}
                        disabled={!available}
                        className={`flex flex-col items-center py-2 rounded-xl text-center transition-all ${selected ? 'bg-purple-600 text-white' : available ? 'bg-zinc-800 hover:bg-zinc-700 text-white' : 'bg-zinc-900 text-zinc-700 opacity-40 cursor-not-allowed'}`}
                        data-testid={`date-${d.toISOString().split('T')[0]}`}>
                        <span className="text-[9px] font-medium">{DAYS_MAP[d.getDay()]}</span>
                        <span className="text-sm font-bold">{d.getDate()}</span>
                      </button>
                    );
                  })}
                </div>
                {availability.length === 0 && (
                  <p className="text-xs text-zinc-500 text-center py-6 mt-2">No availability set by {expert.name}</p>
                )}
                <button onClick={() => { if (selectedDate) setStep(2); }}
                  disabled={!selectedDate}
                  className="w-full mt-4 bg-white text-black font-bold rounded-xl py-3 text-sm disabled:opacity-30 transition-colors"
                  data-testid="booking-next-1">
                  Continue
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Time slot + Duration + Call Type */}
        {step === 2 && selectedDate && (
          <div className="p-4 space-y-4" data-testid="booking-step-time">
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-semibold">Available times — {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
              <div className="grid grid-cols-4 gap-2">
                {getAvailableHours(selectedDate).map(h => (
                  <button key={h} onClick={() => setSelectedSlot(h)}
                    className={`py-2.5 rounded-xl text-xs font-bold transition-all ${selectedSlot === h ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                    data-testid={`slot-${h}`}>
                    {formatHour(h)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-semibold">Call Duration</p>
              <div className="flex gap-2">
                {DURATIONS.map(d => (
                  <button key={d} onClick={() => setDuration(d)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all ${duration === d ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                    data-testid={`duration-${d}`}>
                    {d} min
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-semibold">Call Type</p>
              <div className="flex gap-2">
                <button onClick={() => setCallType('video')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${callType === 'video' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                  data-testid="type-video">
                  <Video className="w-4 h-4" /> Video
                </button>
                <button onClick={() => setCallType('voice')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all ${callType === 'voice' ? 'bg-green-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                  data-testid="type-voice">
                  <Phone className="w-4 h-4" /> Voice
                </button>
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-semibold">Note to Expert <span className="text-zinc-600 font-normal">(optional)</span></p>
              <textarea value={briefing} onChange={e => setBriefing(e.target.value.slice(0, 500))} rows={3}
                placeholder="What would you like to discuss?"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-xs outline-none text-white placeholder-zinc-600 resize-none"
                data-testid="booking-briefing" />
              <p className="text-[9px] text-zinc-600 mt-1 text-right">{briefing.length}/500</p>
            </div>
            <button onClick={() => { if (selectedSlot !== null) setStep(3); }}
              disabled={selectedSlot === null}
              className="w-full bg-white text-black font-bold rounded-xl py-3 text-sm disabled:opacity-30 transition-colors"
              data-testid="booking-next-2">
              Continue to Payment
            </button>
          </div>
        )}

        {/* Step 3: Refund Policy + Payment */}
        {step === 3 && (
          <div className="p-4 space-y-4" data-testid="booking-step-pay">
            {/* Summary */}
            <div className="bg-zinc-800/50 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-orange-400 p-[1.5px] shrink-0">
                  <div className="w-full h-full rounded-full bg-black flex items-center justify-center overflow-hidden">
                    {expert.picture ? <img src={expert.picture} alt="" className="w-full h-full object-cover" /> :
                      <span className="text-xs font-bold">{expert.name?.[0]}</span>}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{expert.name}</p>
                  <p className="text-[10px] text-zinc-500">{callType === 'video' ? 'Video' : 'Voice'} Call · {duration} min</p>
                </div>
              </div>
              <div className="border-t border-zinc-700 pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-xs"><span className="text-zinc-400">Date</span><span className="text-white font-medium">{selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span></div>
                <div className="flex justify-between text-xs"><span className="text-zinc-400">Time</span><span className="text-white font-medium">{formatHour(selectedSlot)}</span></div>
                <div className="flex justify-between text-xs font-bold"><span className="text-zinc-300">Total</span><span className="text-white text-base">₹{totalPrice}</span></div>
              </div>
            </div>

            {/* Refund Policy */}
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3.5" data-testid="refund-policy">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-amber-400 mb-1">Refund & Cancellation Policy</p>
                  <ul className="text-[10px] text-zinc-400 space-y-0.5">
                    <li>• Full refund if cancelled 4+ hours before</li>
                    <li>• 50% refund if cancelled 1-4 hours before</li>
                    <li>• No refund within 1 hour of the call</li>
                    <li>• Full refund if expert doesn't show up</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Booking mode info */}
            {expert.booking_mode === 'request' && (
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-3 flex items-start gap-2">
                <Clock className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <p className="text-[10px] text-purple-300">This expert uses Request Mode. Your booking will be pending until they confirm.</p>
              </div>
            )}

            {/* Payment Options */}
            <div className="space-y-2">
              <button onClick={handlePayRazorpay} disabled={paying}
                className="w-full bg-white text-black font-bold rounded-xl py-3 text-sm disabled:opacity-30 flex items-center justify-center gap-2 transition-colors"
                data-testid="pay-razorpay-btn">
                {paying ? 'Processing...' : `Pay ₹${totalPrice} with Razorpay`}
              </button>
              <button onClick={handlePayWallet} disabled={paying}
                className="w-full bg-zinc-800 border border-zinc-700 text-white font-bold rounded-xl py-3 text-sm disabled:opacity-30 flex items-center justify-center gap-2 hover:bg-zinc-700 transition-colors"
                data-testid="pay-wallet-btn">
                <Wallet className="w-4 h-4" /> {paying ? '...' : `Pay from Wallet`}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Status */}
        {step === 4 && bookingResult && (
          <div className="p-6 text-center space-y-4" data-testid="booking-step-status">
            <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${bookingResult.status === 'pending_approval' ? 'bg-amber-500/20' : 'bg-green-500/20'}`}>
              {bookingResult.status === 'pending_approval' ? (
                <Clock className="w-8 h-8 text-amber-400" />
              ) : (
                <Check className="w-8 h-8 text-green-400" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                {bookingResult.status === 'pending_approval' ? 'Booking Requested' : 'Booking Confirmed!'}
              </h3>
              <p className="text-sm text-zinc-400 mt-1">
                {bookingResult.status === 'pending_approval'
                  ? `Waiting for ${expert.name} to confirm your session`
                  : `Your ${callType} call with ${expert.name} is confirmed`}
              </p>
            </div>
            <div className="bg-zinc-800/50 rounded-xl p-3 text-left space-y-1">
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Date</span><span className="text-white">{bookingResult.slot_date}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Time</span><span className="text-white">{bookingResult.slot_time}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Type</span><span className="text-white capitalize">{bookingResult.call_type}</span></div>
              <div className="flex justify-between text-xs"><span className="text-zinc-500">Amount</span><span className="text-white font-bold">₹{bookingResult.price}</span></div>
            </div>
            <button onClick={onClose}
              className="w-full bg-white text-black font-bold rounded-xl py-3 text-sm transition-colors"
              data-testid="booking-done-btn">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
